import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FilePreviewDialog } from "@/components/upload/FilePreviewDialog";
import {
  AddFilesDialog,
  PipelineBoard,
  type PipelineColumnSelection,
} from "@/components/upload/pipeline";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { AnimatedDialog, ConfirmDialog } from "@/components/reusable";
import {
  MappingWorkbench,
  type MappingWorkbenchFileMappedEvent,
} from "@/components/mapping/MappingWorkbench";
import {
  DataQualityView,
  type DqxRerunStartedEvent,
} from "@/components/data-quality/DataQualityView";
import { KnownMappingsPage } from "@/pages/KnownMappingsPage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  classifyFile,
  cancelBatchExtraction,
  cleanupFileArtifacts,
  deleteUploadedFile,
  dismissPublishedFile,
  downloadMappedFileXlsx,
  fetchBatchExtractionStatus,
  fetchBoardState,
  fetchExtractionWorkflowStatus,
  fetchFileExtractionMetrics,
  fetchMappingSnapshot,
  fetchSchemaFields,
  formatExtractionRunStatusLabel,
  invalidateFilePreviewCache,
  isAbortError,
  loadMappingSelection,
  previewExtraction,
  saveMappingSelection,
  triggerBatchExtraction,
  triggerExtractionWorkflow,
  uploadFile,
  type BatchExtractionStatus,
  type BatchExtractionStatusCategory,
  type BoardStateFilters,
} from "@/services/api";
import {
  BATCH_STARTING_PLACEHOLDER_ID,
  stageKey,
  buildSessionFromBoardFiles,
  createStartingBatchStatus,
  createSingleFileBatchStatus,
  interruptibleSleep,
  isBatchIngestionActive,
  type BatchIngestionSession,
  mergeBatchExtractionStatuses,
  removeFileFromBatchStatus,
  shouldResumeDqxPolling,
} from "@/services/batchIngestionSession";
import {
  clearBatchFileCache,
  registerBatchFiles,
  resolveBatchSourceFile,
  unregisterBatchFile,
} from "@/services/batchFileCache";
import { useUpload } from "@/hooks/useUpload";
import { useUploadSchemas } from "@/hooks/useUploadSchemas";
import { useTelemetry } from "@/hooks/useTelemetry";
import { schemaRequiresReportType } from "@/config/reportType";
import {
  buildDefaultMappingSelection,
  buildMappingSelectionFromSaved,
  buildMappingSelectionPayload,
  buildSeededRegionReportTypes,
  countPipelineSourceRows,
  countPipelineTables,
  buildRegionRowCounts,
  countSelectedMappingWorksheets,
  normalizeWorksheetPreviewMetadata,
} from "@/lib/mappingRegions";
import { finalizePipelineDqxAfterJobTerminal } from "@/lib/pipelineDqxCompletion";
import { hydrateDqxNavigationFromSession } from "@/lib/pipelineDqxNavigation";
import { pollDqxRunUntilTerminal } from "@/lib/pipelineDqxTrigger";
import { applyMappingsPerRegionWithDqx } from "@/lib/pipelineMultiRegionDqx";
import {
  listPipelineDqxDialogRuns,
  replacePipelineDqxRunAtIndex,
  selectPipelineDqxDialogRun,
} from "@/lib/pipelineMultiTableDqxView";
import { nextBatchPollIntervalMs } from "@/lib/pipelinePolling";
import { runWithConcurrencyLimit } from "@/lib/pipelinePreviewCache";
import {
  aggregatePipelineDqxRuns,
  getPipelineFileDqxState,
  IDLE_PIPELINE_DQX_STATE,
  shouldReleaseDqxArtifacts,
  type PipelineDqxPhase,
  type PipelineDqxRunOutcome,
  type PipelineFileDqxState,
} from "@/lib/pipelineDqx";
import {
  buildPipelineBoardModel,
  getPipelineFileStage,
  type PipelineStageId,
} from "@/lib/pipelineBoard";
import { regionFilterKeyForRun } from "@/lib/dqxMergedQuarantine";
import {
  mergeMappingWorkbenchDqxRuns,
  pipelineDqxRunsToWorkbenchRuns,
  type MappingWorkbenchDqxRun,
} from "@/lib/mappingWorkbenchDqx";
import { takePipelineUploadHandoff } from "@/lib/pipelineUploadHandoff";
import { getPipelineBulkActionTargets } from "@/lib/pipelineBulkActions";
import {
  loadPipelineFilePreviewContext,
  type PipelineFilePreviewContext,
} from "@/lib/pipelineFileContext";
import {
  buildSchemaNameByFileId,
  canSubmitPendingPipelineUploads,
  humanSchemaName,
  mergePipelineFileMetaSchemaNames,
  resolvePipelineSchemaDisplayName,
  type PendingPipelineUpload,
  type PipelineFileMeta,
} from "@/lib/pipelineTypes";
import type { KnownMapping, ParsedWorksheet, SchemaField } from "@/types";

const BATCH_POLL_INTERVAL_MS = 2000;
const BATCH_POLL_MAX_ATTEMPTS = 450;
const METRICS_PREFETCH_CONCURRENCY = 3;
const BULK_ACTION_CONCURRENCY = 3;

function pendingFileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function newDropBatchId(): string {
  const uuid =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
  return `batch-${uuid.replace(/-/g, "").slice(0, 8)}`;
}

const DELETE_SCOPE_BY_STAGE: Record<PipelineStageId, string> = {
  upload: "including any extraction results",
  map: "including any extraction results",
  validate: "including extracted data, mapped data, and data-quality results",
  resolve: "including extracted data, mapped data, and data-quality results",
  published: "",
};

interface UploadingPipelineFile {
  key: string;
  fileName: string;
  schemaId: string;
  schemaName?: string | null;
  status: "uploading" | "uploaded";
  fileId?: string;
  providerId?: string | null;
  uploadedBy?: string | null;
}

async function uploadPendingEntries(
  entries: PendingPipelineUpload[],
  signal?: AbortSignal,
  onFileUploaded?: (
    completed: number,
    total: number,
    uploaded: { key: string; documentId: string }
  ) => void | Promise<void>,
  providerId?: string | null
): Promise<
  {
    file: File;
    documentId: string;
    schemaId: string;
    reportType?: string | null;
    allowedReportTypes?: string[];
    providerId?: string | null;
  }[]
> {
  const uploads: {
    file: File;
    documentId: string;
    schemaId: string;
    reportType?: string | null;
    allowedReportTypes?: string[];
    providerId?: string | null;
  }[] = [];
  const total = entries.length;
  for (const [index, entry] of entries.entries()) {
    const confirmation = await uploadFile(entry.file, entry.schemaId, signal, providerId);
    uploads.push({
      file: entry.file,
      documentId: confirmation.documentId,
      schemaId: entry.schemaId,
      reportType: entry.reportType ?? null,
      allowedReportTypes: entry.allowedReportTypes,
      providerId: providerId?.trim() || null,
    });
    await onFileUploaded?.(index + 1, total, {
      key: pendingFileKey(entry.file),
      documentId: confirmation.documentId,
    });
  }
  return uploads;
}

function buildPipelineFileMetaFromUpload(entry: {
  file: File;
  documentId: string;
  schemaId: string;
  schemaName?: string | null;
  reportType?: string | null;
  allowedReportTypes?: string[];
  providerId?: string | null;
  uploadedBy?: string | null;
}): PipelineFileMeta {
  return {
    fileId: entry.documentId,
    fileName: entry.file.name,
    schemaId: entry.schemaId,
    schemaName: entry.schemaName?.trim() || null,
    reportType: entry.reportType ?? null,
    allowedReportTypes: entry.allowedReportTypes,
    providerId: entry.providerId?.trim() || null,
    uploadedBy: entry.uploadedBy?.trim() || null,
  };
}

function toUploadingPipelineFile(
  entry: PendingPipelineUpload,
  schemaNameById: Record<string, string>,
  providerId: string | null,
  uploadedBy: string | null
): UploadingPipelineFile {
  return {
    key: pendingFileKey(entry.file),
    fileName: entry.file.name,
    schemaId: entry.schemaId,
    schemaName: humanSchemaName(entry.schemaId, null, schemaNameById),
    status: "uploading",
    providerId,
    uploadedBy,
  };
}

/**
 * Complex ingestion upload flow (/pipeline).
 * Upload → extract workflow → region preview → mapping.
 * Supports single-file and batch extraction.
 */
export function ComplexIngestionUploadPage() {
  const currentUser = useCurrentUser();
  const isBoardAdmin = Boolean(currentUser?.roles.includes("App.ADMIN"));
  // Multi-provider App.USER needs the provider scope dropdown; single-provider does not.
  const boardProviderIds = currentUser?.providerIds ?? [];
  const showProviderBoardFilter = isBoardAdmin || boardProviderIds.length > 1;
  const {
    confirmUpload,
    resetUpload,
    saveMappings,
    setDqxRunInfo,
    setClassifying,
    setClassificationResult,
    setReportType,
    reportType,
    detectedType,
    allowedReportTypes,
    isClassifying,
    documentId: uploadDocumentId,
  } = useUpload();
  // Unscoped list keeps board / preview schema names available for any provider.
  const { schemas, error: schemaError } = useUploadSchemas();
  // Dialog list is Provide Schems Access-scoped to the selected provider.
  const [addFilesProviderId, setAddFilesProviderId] = useState<string | null>(null);
  const { schemas: addFilesSchemas, isLoadingSchemas } = useUploadSchemas(addFilesProviderId);
  const { track, trackError, startTimer } = useTelemetry();
  const resumeAttempted = useRef(false);
  const batchAbortRef = useRef<AbortController | null>(null);
  const batchStatusRef = useRef<BatchExtractionStatus | null>(null);
  const dropUploadsRemainingRef = useRef(0);
  const activeDropBatchIdRef = useRef<string | null>(null);
  const dropPollInFlightRef = useRef<Promise<void> | null>(null);
  const metricsPrefetchStartedRef = useRef<Set<string>>(new Set());
  /** Provider stamped on the current upload batch (Add files dialog / Home handoff). */
  const sessionProviderIdRef = useRef<string | null>(null);
  const dqxPollAbortByFileRef = useRef<Map<string, AbortController>>(new Map());
  const pendingClassifyAbortRef = useRef<Map<string, AbortController>>(new Map());
  const pendingPipelineActionFileIdsRef = useRef<Set<string>>(new Set());
  const schemaFieldsCacheRef = useRef<{ schemaId: string; fields: SchemaField[] } | null>(null);
  const boardScopeFiltersReadyRef = useRef(false);

  const [boardFilterProviderId, setBoardFilterProviderId] = useState<string | null>(null);
  const [boardFilterUploadedBy, setBoardFilterUploadedBy] = useState<string | null>(null);

  const boardStateFilters: BoardStateFilters = useMemo(
    () => ({
      providerId: boardFilterProviderId,
      uploadedBy: boardFilterUploadedBy,
    }),
    [boardFilterProviderId, boardFilterUploadedBy]
  );

  // The upload context outlives route changes (provider mounts at App level);
  // the board hydrates it per file, so a fresh visit always starts clean.
  useEffect(() => {
    resetUpload();
  }, [resetUpload]);

  const [files, setFiles] = useState<File[]>([]);
  const file = files[0] ?? null;
  const isBatchMode = files.length > 1;

  const [defaultSchemaId, setDefaultSchemaId] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [worksheets, setWorksheets] = useState<ParsedWorksheet[]>([]);
  const [requiresReview, setRequiresReview] = useState(false);
  const [reviewReason, setReviewReason] = useState<string | null>(null);
  const [reviewThreshold, setReviewThreshold] = useState(0.75);
  const [globalConfidence, setGlobalConfidence] = useState<number | null>(null);
  const [processingDurationMs, setProcessingDurationMs] = useState<number | null>(null);
  const [ingestionTriggerSkipped, setIngestionTriggerSkipped] = useState(false);
  const [executionMode, setExecutionMode] = useState<string | null>(null);
  const [isTriggeringIngestion, setIsTriggeringIngestion] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocumentId, setUploadedDocumentId] = useState("");
  const [uploadedFileSchemaId, setUploadedFileSchemaId] = useState("");
  const [uploadedFileReportType, setUploadedFileReportType] = useState<string | null>(null);
  const [uploadedFileAllowedReportTypes, setUploadedFileAllowedReportTypes] = useState<string[]>(
    []
  );
  const [previewFileName, setPreviewFileName] = useState("");
  const [ingestionStatusLabel, setIngestionStatusLabel] = useState<string | null>(null);
  const [flowError, setFlowError] = useState("");
  /** When set, hides the board error banner until a different error string appears. */
  const [dismissedBoardError, setDismissedBoardError] = useState<string | null>(null);

  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchExtractionStatus | null>(null);
  const [batchFileMeta, setBatchFileMeta] = useState<PipelineFileMeta[]>([]);
  const [mappedFileIds, setMappedFileIds] = useState<string[]>([]);
  const [structureConfirmedFileIds, setStructureConfirmedFileIds] = useState<string[]>([]);
  const [, setBatchStartedAt] = useState<string | null>(null);
  const [isPollingBatch, setIsPollingBatch] = useState(false);
  const [isRestoringBoard, setIsRestoringBoard] = useState(true);
  const [previewingFileId, setPreviewingFileId] = useState<string | null>(null);
  const [regionReportTypes, setRegionReportTypes] = useState<Record<string, string | null>>({});
  const [mappingSelection, setMappingSelection] = useState<Record<string, boolean>>({});
  const [addFilesDialogOpen, setAddFilesDialogOpen] = useState(false);
  const [pendingAddEntries, setPendingAddEntries] = useState<PendingPipelineUpload[]>([]);
  const [singleFileExtractionCategory, setSingleFileExtractionCategory] =
    useState<BatchExtractionStatusCategory | null>(null);
  const [fileRowCounts, setFileRowCounts] = useState<Record<string, number>>({});
  const [fileTableCounts, setFileTableCounts] = useState<Record<string, number>>({});
  /** Per-file map of regionId → source row count for multi-table DQ denominators. */
  const [fileRegionRowCounts, setFileRegionRowCounts] = useState<
    Record<string, Record<string, number>>
  >({});
  const [fileGlobalConfidence, setFileGlobalConfidence] = useState<Record<string, number>>({});
  const [fileRequiresReview, setFileRequiresReview] = useState<Record<string, boolean>>({});
  const [pipelineDqxByFileId, setPipelineDqxByFileId] = useState<
    Record<string, PipelineFileDqxState>
  >({});
  const [pendingPipelineActionByFileId, setPendingPipelineActionByFileId] = useState<
    Record<string, "mapping" | "dqx" | "confirm" | "download">
  >({});
  const [bulkProgress, setBulkProgress] = useState<{ label: string } | null>(null);
  const [flowInfo, setFlowInfo] = useState("");
  const [activeWorkflowFileCount, setActiveWorkflowFileCount] = useState(0);
  const [uploadedWorkflowFileCount, setUploadedWorkflowFileCount] = useState(0);
  const [workflowBatchStatus, setWorkflowBatchStatus] = useState<BatchExtractionStatus | null>(
    null
  );
  const [uploadingFiles, setUploadingFiles] = useState<UploadingPipelineFile[]>([]);
  const [deleteFileTarget, setDeleteFileTarget] = useState<{
    fileId: string;
    fileName: string;
    stage: PipelineStageId;
  } | null>(null);
  const [selectedValidateIds, setSelectedValidateIds] = useState<string[]>([]);
  const [selectedConfirmIds, setSelectedConfirmIds] = useState<string[]>([]);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [dqxDialogOpen, setDqxDialogOpen] = useState(false);
  /** Card the DQ dialog was opened for; guards against a stale upload context. */
  const [dqxDialogFileId, setDqxDialogFileId] = useState<string | null>(null);
  const [dqxDialogAutoRetrieve, setDqxDialogAutoRetrieve] = useState(false);
  const [dqxDialogInitialTableIndex, setDqxDialogInitialTableIndex] = useState(0);
  const [knownMappingsDialogOpen, setKnownMappingsDialogOpen] = useState(false);
  // Captured when the dialog opens so KnownMappingsPage is scoped to this batch's provider.
  const [knownMappingsDialogProviderId, setKnownMappingsDialogProviderId] = useState<
    string | undefined
  >(undefined);
  // In-memory only: the backend cleanup endpoint is idempotent, so repeating
  // a cleanup after a reload is harmless.
  const cleanedStagesRef = useRef<Set<string>>(new Set());
  // Uploads the user removed mid-flight, keyed by pendingFileKey. deleteRequested
  // guards against double-deleting once the upload finishes and yields a documentId.
  const removedUploadsRef = useRef<Map<string, { deleteRequested: boolean }>>(new Map());

  useEffect(() => {
    const dqxPollAborts = dqxPollAbortByFileRef.current;
    const pendingClassifyAborts = pendingClassifyAbortRef.current;
    return () => {
      for (const controller of dqxPollAborts.values()) {
        controller.abort();
      }
      dqxPollAborts.clear();
      for (const controller of pendingClassifyAborts.values()) {
        controller.abort();
      }
      pendingClassifyAborts.clear();
    };
  }, []);

  const error = schemaError || flowError;
  const boardError = error && error !== dismissedBoardError ? error : "";

  useEffect(() => {
    if (!error) setDismissedBoardError(null);
  }, [error]);

  const applyPreviewWorksheets = useCallback(
    (
      previewWorksheets: ParsedWorksheet[],
      selection?: Record<string, boolean>,
      seedReportType?: string | null
    ) => {
      const normalized = normalizeWorksheetPreviewMetadata(previewWorksheets);
      const resolvedSelection = selection ?? buildDefaultMappingSelection(normalized);
      setWorksheets(normalized);
      setMappingSelection(resolvedSelection);
      const mappingCount = countSelectedMappingWorksheets(normalized, resolvedSelection);
      if (mappingCount > 1) {
        setReportType(null);
        setRegionReportTypes(
          buildSeededRegionReportTypes(normalized, resolvedSelection, seedReportType)
        );
      } else {
        setRegionReportTypes({});
        if (seedReportType && seedReportType !== "unknown") {
          setReportType(seedReportType);
        }
      }
    },
    [setReportType]
  );

  const resetPreviewState = useCallback(() => {
    setWorksheets([]);
    setRequiresReview(false);
    setReviewReason(null);
    setGlobalConfidence(null);
    setReviewThreshold(0.75);
    setProcessingDurationMs(null);
    setIngestionTriggerSkipped(false);
    setUploadedDocumentId("");
    setUploadedFileSchemaId("");
    setUploadedFileReportType(null);
    setUploadedFileAllowedReportTypes([]);
    setPreviewFileName("");
    setIngestionStatusLabel(null);
    setRegionReportTypes({});
    setMappingSelection({});
  }, []);

  const resetBatchState = useCallback(() => {
    setBatchId(null);
    setBatchStatus(null);
    setBatchFileMeta([]);
    setMappedFileIds([]);
    setStructureConfirmedFileIds([]);
    setFileRowCounts({});
    setFileTableCounts({});
    setFileRegionRowCounts({});
    setPipelineDqxByFileId({});
    for (const controller of dqxPollAbortByFileRef.current.values()) {
      controller.abort();
    }
    dqxPollAbortByFileRef.current.clear();
    setBatchStartedAt(null);
    setIsPollingBatch(false);
    clearBatchFileCache();
    cleanedStagesRef.current = new Set();
    metricsPrefetchStartedRef.current = new Set();
    dropUploadsRemainingRef.current = 0;
    activeDropBatchIdRef.current = null;
    dropPollInFlightRef.current = null;
    batchStatusRef.current = null;
    setActiveWorkflowFileCount(0);
    setUploadedWorkflowFileCount(0);
    setWorkflowBatchStatus(null);
    setUploadingFiles([]);
  }, []);

  const resolveFileSchemaId = useCallback(
    (fileId: string): string => {
      const fromMeta = batchFileMeta.find((entry) => entry.fileId === fileId)?.schemaId;
      if (fromMeta) return fromMeta;
      if (fileId === uploadedDocumentId && uploadedFileSchemaId) return uploadedFileSchemaId;
      return defaultSchemaId;
    },
    [batchFileMeta, defaultSchemaId, uploadedDocumentId, uploadedFileSchemaId]
  );

  const schemaNameById = useMemo(
    () => Object.fromEntries(schemas.map((schema) => [schema.id, schema.name])),
    [schemas]
  );
  const pickerSchemaNameById = useMemo(
    () => ({
      ...schemaNameById,
      ...Object.fromEntries(addFilesSchemas.map((schema) => [schema.id, schema.name])),
    }),
    [addFilesSchemas, schemaNameById]
  );

  const classifyPendingEntry = useCallback(
    async (fileKey: string, file: File, schemaId: string) => {
      pendingClassifyAbortRef.current.get(fileKey)?.abort();
      const controller = new AbortController();
      pendingClassifyAbortRef.current.set(fileKey, controller);

      const schemaName = schemaNameById[schemaId] ?? schemaId;
      if (!schemaRequiresReportType(schemaName)) {
        setPendingAddEntries((current) =>
          current.map((entry) =>
            pendingFileKey(entry.file) === fileKey
              ? {
                  ...entry,
                  reportType: null,
                  allowedReportTypes: [],
                  detectedType: null,
                  isClassifying: false,
                }
              : entry
          )
        );
        return;
      }

      setPendingAddEntries((current) =>
        current.map((entry) =>
          pendingFileKey(entry.file) === fileKey
            ? {
                ...entry,
                isClassifying: true,
                reportType: null,
                allowedReportTypes: [],
                detectedType: null,
              }
            : entry
        )
      );

      try {
        const result = await classifyFile(file, schemaId, controller.signal);
        if (controller.signal.aborted) return;
        const autoReportType = result.detectedType !== "unknown" ? result.detectedType : null;
        setPendingAddEntries((current) =>
          current.map((entry) =>
            pendingFileKey(entry.file) === fileKey
              ? {
                  ...entry,
                  isClassifying: false,
                  allowedReportTypes: result.allowedReportTypes,
                  detectedType: result.detectedType,
                  reportType: autoReportType,
                }
              : entry
          )
        );
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) return;
        setPendingAddEntries((current) =>
          current.map((entry) =>
            pendingFileKey(entry.file) === fileKey
              ? {
                  ...entry,
                  isClassifying: false,
                  allowedReportTypes: [],
                  detectedType: null,
                  reportType: null,
                }
              : entry
          )
        );
        trackError(err, { context: "pending_file_classify", schemaId });
      } finally {
        pendingClassifyAbortRef.current.delete(fileKey);
      }
    },
    [schemaNameById, trackError]
  );

  const handlePendingFilesAdd = useCallback(
    (nextFiles: File[]) => {
      if (!addFilesProviderId) {
        setFlowError("Select a provider first.");
        return;
      }
      const previousKeys = new Set(pendingAddEntries.map((entry) => pendingFileKey(entry.file)));
      const nextEntries = nextFiles.map((file) => {
        const existing = pendingAddEntries.find(
          (entry) => pendingFileKey(entry.file) === pendingFileKey(file)
        );
        return existing ?? { file, schemaId: defaultSchemaId };
      });
      setPendingAddEntries(nextEntries);
      // Do not clear flowError here — MultiFileUploadField owns limit messages via
      // onUploadError and must not be overwritten after truncate.

      for (const entry of nextEntries) {
        const fileKey = pendingFileKey(entry.file);
        if (previousKeys.has(fileKey) || !entry.schemaId) continue;
        const schemaName = schemaNameById[entry.schemaId] ?? entry.schemaId;
        if (schemaRequiresReportType(schemaName)) {
          void classifyPendingEntry(fileKey, entry.file, entry.schemaId);
        }
      }

      if (nextFiles.length > 0) {
        const firstFile = nextFiles[0];
        if (firstFile) {
          track("file_selected", {
            fileCount: nextFiles.length,
            fileExtension: firstFile.name.split(".").pop()?.toLowerCase() ?? "unknown",
            fileSizeKB: Math.round(nextFiles.reduce((sum, f) => sum + f.size, 0) / 1024),
            batchMode: nextFiles.length > 1,
            appendMode: batchFileMeta.length > 0 || !!uploadedDocumentId,
          });
        }
      }
    },
    [
      addFilesProviderId,
      batchFileMeta.length,
      classifyPendingEntry,
      defaultSchemaId,
      pendingAddEntries,
      schemaNameById,
      track,
      uploadedDocumentId,
    ]
  );

  const handleAddFilesProviderChange = useCallback(
    (nextProviderId: string | null) => {
      setAddFilesProviderId(nextProviderId);
      sessionProviderIdRef.current = nextProviderId;
      setDefaultSchemaId("");
      setPendingAddEntries((current) =>
        current.map((entry) => ({
          file: entry.file,
          schemaId: "",
        }))
      );
      setFlowError("");
      track("provider_selected", { providerId: nextProviderId ?? "none", context: "add_files" });
    },
    [track]
  );

  const openAddFilesDialog = useCallback(() => {
    setPendingAddEntries([]);
    setFlowInfo("");
    // Prefill from last stamp / handoff, but keep the selector editable (2B).
    setAddFilesProviderId(sessionProviderIdRef.current);
    setAddFilesDialogOpen(true);
  }, []);

  const handlePendingEntrySchemaChange = useCallback(
    (index: number, schemaId: string) => {
      setDefaultSchemaId(schemaId);
      const entry = pendingAddEntries[index];
      if (!entry) return;

      const fileKey = pendingFileKey(entry.file);
      const schemaName = schemaNameById[schemaId] ?? schemaId;
      const needsReportType = schemaRequiresReportType(schemaName);

      if (!needsReportType) {
        pendingClassifyAbortRef.current.get(fileKey)?.abort();
      }

      setPendingAddEntries((current) =>
        current.map((item, entryIndex) =>
          entryIndex === index
            ? {
                file: item.file,
                schemaId,
                reportType: needsReportType ? null : undefined,
                allowedReportTypes: needsReportType ? [] : undefined,
                detectedType: needsReportType ? null : undefined,
                isClassifying: needsReportType,
              }
            : item
        )
      );

      if (needsReportType) {
        void classifyPendingEntry(fileKey, entry.file, schemaId);
      }
    },
    [classifyPendingEntry, pendingAddEntries, schemaNameById]
  );

  const handlePendingAllSchemasChange = useCallback(
    (schemaId: string) => {
      if (schemaId) setDefaultSchemaId(schemaId);
      const schemaName = schemaNameById[schemaId] ?? schemaId;
      const needsReportType = !!schemaId && schemaRequiresReportType(schemaName);

      if (!needsReportType) {
        for (const entry of pendingAddEntries) {
          pendingClassifyAbortRef.current.get(pendingFileKey(entry.file))?.abort();
        }
      }

      setPendingAddEntries((current) =>
        current.map((item) => ({
          file: item.file,
          schemaId,
          reportType: needsReportType ? null : undefined,
          allowedReportTypes: needsReportType ? [] : undefined,
          detectedType: needsReportType ? null : undefined,
          isClassifying: needsReportType,
        }))
      );

      if (needsReportType) {
        for (const entry of pendingAddEntries) {
          void classifyPendingEntry(pendingFileKey(entry.file), entry.file, schemaId);
        }
      }
    },
    [classifyPendingEntry, pendingAddEntries, schemaNameById]
  );

  const handlePendingEntryReportTypeChange = useCallback((index: number, reportType: string) => {
    setPendingAddEntries((current) =>
      current.map((entry, entryIndex) => (entryIndex === index ? { ...entry, reportType } : entry))
    );
  }, []);

  const handlePendingEntryRemove = useCallback(
    (index: number) => {
      const entry = pendingAddEntries[index];
      if (entry) {
        pendingClassifyAbortRef.current.get(pendingFileKey(entry.file))?.abort();
      }
      setPendingAddEntries((current) => current.filter((_, entryIndex) => entryIndex !== index));
    },
    [pendingAddEntries]
  );

  const seedReportTypeFromMeta = useCallback(
    (fileId: string) => {
      const meta =
        batchFileMeta.find((entry) => entry.fileId === fileId) ??
        (uploadedDocumentId === fileId
          ? {
              reportType: uploadedFileReportType,
              allowedReportTypes: uploadedFileAllowedReportTypes,
            }
          : null);
      if (!meta?.reportType) return;

      setReportType(meta.reportType);
      setClassificationResult({
        detectedType: meta.reportType,
        allowedReportTypes:
          meta.allowedReportTypes && meta.allowedReportTypes.length > 0
            ? meta.allowedReportTypes
            : [meta.reportType],
      });
    },
    [
      batchFileMeta,
      setClassificationResult,
      setReportType,
      uploadedDocumentId,
      uploadedFileAllowedReportTypes,
      uploadedFileReportType,
    ]
  );

  const recordFilePreviewMetrics = useCallback(
    (fileId: string, previewWorksheets: ParsedWorksheet[], selection?: Record<string, boolean>) => {
      const rowCount = countPipelineSourceRows(previewWorksheets, selection);
      const tableCount = countPipelineTables(previewWorksheets, selection);
      const regionRowCounts = buildRegionRowCounts(previewWorksheets, selection);
      setFileRowCounts((current) => ({ ...current, [fileId]: rowCount }));
      setFileTableCounts((current) => ({ ...current, [fileId]: tableCount }));
      setFileRegionRowCounts((current) => ({ ...current, [fileId]: regionRowCounts }));
      return { rowCount, tableCount, regionRowCounts };
    },
    []
  );

  const recordFileConfidence = useCallback(
    (
      fileId: string,
      globalConfidence: number | null | undefined,
      requiresReview?: boolean | null
    ) => {
      if (typeof globalConfidence === "number") {
        setFileGlobalConfidence((current) => ({ ...current, [fileId]: globalConfidence }));
        setFileRequiresReview((current) => ({
          ...current,
          [fileId]: requiresReview === true,
        }));
      }
    },
    []
  );

  const prefetchPipelinePreviewMetrics = useCallback(async (status: BatchExtractionStatus) => {
    const knownRowCounts = { ...fileRowCountsRef.current };
    const knownTableCounts = { ...fileTableCountsRef.current };
    const knownConfidence = { ...fileGlobalConfidenceRef.current };

    const rowUpdates: Record<string, number> = {};
    const tableUpdates: Record<string, number> = {};
    const confidenceUpdates: Record<string, number> = {};
    const requiresReviewUpdates: Record<string, boolean> = {};

    for (const item of status.items) {
      if (item.statusCategory !== "succeeded") continue;
      if (item.rowCount != null && item.tableCount != null) {
        if (knownRowCounts[item.fileId] == null) rowUpdates[item.fileId] = item.rowCount;
        if (knownTableCounts[item.fileId] == null) tableUpdates[item.fileId] = item.tableCount;
      }
      if (typeof item.globalConfidence === "number" && knownConfidence[item.fileId] == null) {
        confidenceUpdates[item.fileId] = item.globalConfidence;
        requiresReviewUpdates[item.fileId] = item.requiresReview === true;
      }
    }

    const missingIds = status.items
      .filter((item) => item.statusCategory === "succeeded")
      .map((item) => item.fileId)
      .filter((fileId) => !metricsPrefetchStartedRef.current.has(fileId))
      .filter(
        (fileId) =>
          ((knownRowCounts[fileId] == null || knownTableCounts[fileId] == null) &&
            rowUpdates[fileId] == null) ||
          (knownConfidence[fileId] == null && confidenceUpdates[fileId] == null)
      );
    for (const fileId of missingIds) {
      metricsPrefetchStartedRef.current.add(fileId);
    }

    await runWithConcurrencyLimit(missingIds, METRICS_PREFETCH_CONCURRENCY, async (fileId) => {
      try {
        const metrics = await fetchFileExtractionMetrics(fileId);
        if (knownRowCounts[fileId] == null && rowUpdates[fileId] == null) {
          rowUpdates[fileId] = metrics.rowCount;
        }
        if (knownTableCounts[fileId] == null && tableUpdates[fileId] == null) {
          tableUpdates[fileId] = metrics.tableCount;
        }
        if (
          typeof metrics.globalConfidence === "number" &&
          knownConfidence[fileId] == null &&
          confidenceUpdates[fileId] == null
        ) {
          confidenceUpdates[fileId] = metrics.globalConfidence;
          requiresReviewUpdates[fileId] = metrics.requiresReview;
        }
      } catch {
        // Preview metrics are optional display metadata.
      }
    });

    if (Object.keys(rowUpdates).length > 0) {
      setFileRowCounts((current) => ({ ...current, ...rowUpdates }));
    }
    if (Object.keys(tableUpdates).length > 0) {
      setFileTableCounts((current) => ({ ...current, ...tableUpdates }));
    }
    if (Object.keys(confidenceUpdates).length > 0) {
      setFileGlobalConfidence((current) => ({ ...current, ...confidenceUpdates }));
      setFileRequiresReview((current) => ({ ...current, ...requiresReviewUpdates }));
    }
  }, []);

  const updatePipelineDqxState = useCallback((fileId: string, state: PipelineFileDqxState) => {
    setPipelineDqxByFileId((current) => ({ ...current, [fileId]: state }));
  }, []);

  // Fresh-read mirrors for long-lived async loops (DQ polling) that outlive
  // the render closure they were created in.
  const pipelineDqxByFileIdRef = useRef(pipelineDqxByFileId);
  useEffect(() => {
    pipelineDqxByFileIdRef.current = pipelineDqxByFileId;
  }, [pipelineDqxByFileId]);
  const fileRowCountsRef = useRef(fileRowCounts);
  useEffect(() => {
    fileRowCountsRef.current = fileRowCounts;
  }, [fileRowCounts]);
  const fileRegionRowCountsRef = useRef(fileRegionRowCounts);
  useEffect(() => {
    fileRegionRowCountsRef.current = fileRegionRowCounts;
  }, [fileRegionRowCounts]);
  const fileTableCountsRef = useRef(fileTableCounts);
  useEffect(() => {
    fileTableCountsRef.current = fileTableCounts;
  }, [fileTableCounts]);
  const fileGlobalConfidenceRef = useRef(fileGlobalConfidence);
  useEffect(() => {
    fileGlobalConfidenceRef.current = fileGlobalConfidence;
  }, [fileGlobalConfidence]);
  const fileRequiresReviewRef = useRef(fileRequiresReview);
  useEffect(() => {
    fileRequiresReviewRef.current = fileRequiresReview;
  }, [fileRequiresReview]);
  useEffect(() => {
    batchStatusRef.current = batchStatus;
  }, [batchStatus]);

  /** Mappings applied this visit, keyed by file — server snapshot is the fallback. */
  const mappedFileSnapshotsRef = useRef<Record<string, KnownMapping[]>>({});

  const buildLoadContextParams = useCallback(
    (fileId: string) => ({
      fileId,
      files,
      file,
      batchFileMeta,
      uploadedDocumentId,
      previewFileName,
      cachedWorksheets: worksheets,
      cachedMappingSelection: mappingSelection,
      cachedReportType: reportType,
      cachedRegionReportTypes: regionReportTypes,
      selectedSchema: resolveFileSchemaId(fileId),
      schemas,
      onClassifyStart: () => setClassifying(true),
      onClassifyEnd: () => setClassifying(false),
      onClassificationResult: setClassificationResult,
      onClassifyError: (err: unknown) => {
        trackError(err, { context: "file_classify", schemaId: resolveFileSchemaId(fileId) });
      },
    }),
    [
      batchFileMeta,
      file,
      files,
      mappingSelection,
      previewFileName,
      regionReportTypes,
      reportType,
      resolveFileSchemaId,
      schemas,
      setClassificationResult,
      setClassifying,
      trackError,
      uploadedDocumentId,
      worksheets,
    ]
  );

  const confirmPipelineFileContext = useCallback(
    (ctx: PipelineFilePreviewContext) => {
      confirmUpload({
        file: ctx.sourceFile,
        fileName: ctx.displayName,
        documentId: ctx.fileId,
        schemaId: ctx.schemaId,
        schemaName: ctx.schemaName,
        worksheets: ctx.worksheets,
        mappingSelection: ctx.mappingSelection,
        reportType: ctx.isMultiRegion ? null : ctx.reportType,
        ...(ctx.isMultiRegion ? { regionReportTypes: ctx.regionReportTypes } : {}),
      });
    },
    [confirmUpload]
  );

  const applyPipelineFileContext = useCallback(
    (ctx: PipelineFilePreviewContext) => {
      confirmUpload({
        file: ctx.sourceFile,
        fileName: ctx.displayName,
        documentId: ctx.fileId,
        schemaId: ctx.schemaId,
        schemaName: ctx.schemaName,
        worksheets: ctx.worksheets,
        mappingSelection: ctx.mappingSelection,
        reportType: ctx.isMultiRegion ? null : ctx.reportType,
        ...(ctx.isMultiRegion ? { regionReportTypes: ctx.regionReportTypes } : {}),
      });
      const snapshot = mappedFileSnapshotsRef.current[ctx.fileId];
      if (snapshot?.length) {
        saveMappings(snapshot);
      }
    },
    [confirmUpload, saveMappings]
  );

  const loadSchemaFieldsForMappings = useCallback(async (schemaId: string) => {
    if (schemaFieldsCacheRef.current?.schemaId === schemaId) {
      return schemaFieldsCacheRef.current.fields;
    }
    const fields = await fetchSchemaFields(schemaId);
    schemaFieldsCacheRef.current = { schemaId, fields };
    return fields;
  }, []);

  const startDqxPolling = useCallback(
    (
      fileId: string,
      runsInput: {
        regionId?: string | null;
        runId: number;
        jobId: string;
        message?: string | null;
        phase?: PipelineDqxPhase;
        rowsPassing?: number | null;
        rowsWithIssues?: number | null;
        totalRowsChecked?: number | null;
        error?: string | null;
        sourceRowIndexStart?: number | null;
      }[]
    ) => {
      if (runsInput.length === 0) return;
      dqxPollAbortByFileRef.current.get(fileId)?.abort();
      const controller = new AbortController();
      dqxPollAbortByFileRef.current.set(fileId, controller);

      const initialRuns: PipelineDqxRunOutcome[] = runsInput.map((run) => ({
        regionId: run.regionId ?? null,
        runId: run.runId,
        jobId: run.jobId,
        phase: run.phase ?? "running",
        message: run.message ?? null,
        rowsPassing: run.rowsPassing,
        rowsWithIssues: run.rowsWithIssues,
        totalRowsChecked: run.totalRowsChecked,
        error: run.error,
        sourceRowIndexStart: run.sourceRowIndexStart ?? null,
      }));
      updatePipelineDqxState(fileId, aggregatePipelineDqxRuns(initialRuns));

      void (async () => {
        const fileRowCount = fileRowCountsRef.current[fileId];
        const regionCounts = fileRegionRowCountsRef.current[fileId] ?? {};
        const settled: PipelineDqxRunOutcome[] = [...initialRuns];

        const pollOne = async (index: number) => {
          const seed = initialRuns[index]!;
          if (seed.phase !== "running" && seed.phase !== "idle") return;
          try {
            const outcome = await pollDqxRunUntilTerminal(seed.runId, {
              signal: controller.signal,
            });
            const regionId = seed.regionId?.trim() || "";
            const perRegionCount =
              regionId && regionCounts[regionId] != null ? regionCounts[regionId] : undefined;
            const rowCountForRun =
              perRegionCount ?? (initialRuns.length === 1 ? fileRowCount : undefined);
            const finalized = await finalizePipelineDqxAfterJobTerminal(
              {
                phase: "running",
                runId: seed.runId,
                jobId: seed.jobId,
                message: "Retrieving DQ results…",
              },
              rowCountForRun,
              { jobFailed: outcome !== "success" }
            );
            settled[index] = {
              ...seed,
              phase: finalized.phase,
              rowsPassing: finalized.rowsPassing,
              rowsWithIssues: finalized.rowsWithIssues,
              totalRowsChecked: finalized.totalRowsChecked,
              message: finalized.message ?? null,
              error: finalized.error ?? null,
            };
          } catch (err) {
            if (isAbortError(err)) throw err;
            settled[index] = {
              ...seed,
              phase: "failed",
              error: err instanceof Error ? err.message : String(err),
            };
            trackError(err, { context: "pipeline_dqx_poll", fileId, runId: seed.runId });
          }
          updatePipelineDqxState(fileId, aggregatePipelineDqxRuns(settled));
        };

        try {
          await Promise.all(initialRuns.map((_, index) => pollOne(index)));
          const finalState = aggregatePipelineDqxRuns(settled);
          updatePipelineDqxState(fileId, finalState);
          if (finalState.phase === "failed") {
            track("pipeline_dqx_failed", {
              fileId,
              runCount: settled.length,
              failedCount: settled.filter((r) => r.phase === "failed").length,
            });
          } else {
            track("pipeline_dqx_completed", {
              fileId,
              runCount: settled.length,
              rowsWithIssues: finalState.rowsWithIssues ?? undefined,
            });
          }
        } catch (err) {
          if (isAbortError(err)) return;
          const current = pipelineDqxByFileIdRef.current[fileId];
          if (current?.phase === "running") {
            updatePipelineDqxState(fileId, {
              ...current,
              phase: "failed",
              error: err instanceof Error ? err.message : String(err),
            });
          }
          trackError(err, { context: "pipeline_dqx_poll", fileId });
        } finally {
          dqxPollAbortByFileRef.current.delete(fileId);
        }
      })();
    },
    [track, trackError, updatePipelineDqxState]
  );

  const pollBatchUntilComplete = useCallback(
    async (
      activeBatchId: string,
      signal?: AbortSignal,
      options?: {
        mergeWith?: BatchExtractionStatus | null;
        mergeOptions?: { primaryBatchId?: string; replaceExistingFileIds?: boolean };
        onWorkflowStatus?: (status: BatchExtractionStatus) => void;
        initialStatus?: BatchExtractionStatus;
        expectedFileCount?: number;
        keepPolling?: () => boolean;
        getMergeBase?: () => BatchExtractionStatus | null | undefined;
      }
    ): Promise<BatchExtractionStatus> => {
      let status: BatchExtractionStatus | null = options?.initialStatus ?? null;
      let unchangedStreak = 0;
      let lastActiveCount: number | null = null;

      for (let attempt = 0; attempt < BATCH_POLL_MAX_ATTEMPTS; attempt += 1) {
        if (signal?.aborted) {
          throw new DOMException("Batch polling aborted", "AbortError");
        }

        if (!status) {
          status = await fetchBatchExtractionStatus(activeBatchId, signal);
        }

        const rawStatus = status;

        // An empty status for a batch we know has files means the queue rows are
        // not visible yet; publishing it would wipe the board and end polling early.
        if ((options?.expectedFileCount ?? 0) > 0 && rawStatus.items.length === 0) {
          await interruptibleSleep(nextBatchPollIntervalMs(unchangedStreak), signal);
          status = null;
          continue;
        }

        options?.onWorkflowStatus?.(rawStatus);
        const mergeBase = options?.getMergeBase?.() ?? options?.mergeWith ?? null;
        const boardStatus = mergeBase
          ? mergeBatchExtractionStatuses(mergeBase, rawStatus, options?.mergeOptions)
          : rawStatus;
        setBatchStatus(boardStatus);
        batchStatusRef.current = boardStatus;
        void prefetchPipelinePreviewMetrics(boardStatus);

        const uploadsStillInFlight = options?.keepPolling?.() === true;
        if (!isBatchIngestionActive(rawStatus) && !uploadsStillInFlight) {
          return rawStatus;
        }

        const activeCount = rawStatus.pendingCount + rawStatus.runningCount;
        if (lastActiveCount === activeCount) {
          unchangedStreak += 1;
        } else {
          unchangedStreak = 0;
          lastActiveCount = activeCount;
        }

        await interruptibleSleep(nextBatchPollIntervalMs(unchangedStreak), signal);
        status = null;
      }
      throw new Error("Batch ingestion polling timed out");
    },
    [prefetchPipelinePreviewMetrics]
  );

  const clearActiveBatchWorkflow = useCallback(async () => {
    batchAbortRef.current?.abort();
    for (const controller of dqxPollAbortByFileRef.current.values()) {
      controller.abort();
    }
    dqxPollAbortByFileRef.current.clear();
    for (const controller of pendingClassifyAbortRef.current.values()) {
      controller.abort();
    }
    pendingClassifyAbortRef.current.clear();

    const activeBatchId = batchId;
    if (activeBatchId && activeBatchId !== BATCH_STARTING_PLACEHOLDER_ID) {
      try {
        await cancelBatchExtraction(activeBatchId);
      } catch (err) {
        trackError(err, {
          context: "cancel_batch_extraction",
          batchId: activeBatchId,
        });
      }
    }
    batchAbortRef.current = null;
    resetBatchState();
    setIsUploading(false);
    setIsTriggeringIngestion(false);
    setIsPollingBatch(false);
    setActiveWorkflowFileCount(0);
    setUploadedWorkflowFileCount(0);
    setWorkflowBatchStatus(null);
    setFlowError("");
  }, [batchId, resetBatchState, trackError]);

  const handleCancelBatch = useCallback(async () => {
    track("batch_ingestion_cancelled", {
      batchId: batchId ?? undefined,
    });
    await clearActiveBatchWorkflow();
  }, [batchId, clearActiveBatchWorkflow, track]);

  const handleRemovePipelineFile = useCallback(
    (fileId: string) => {
      track("pipeline_file_removed", {
        fileId,
        batchMode: isBatchMode,
        schemaId: resolveFileSchemaId(fileId),
      });

      const metaIndex = batchFileMeta.findIndex((entry) => entry.fileId === fileId);
      const isTrackedBatchFile = metaIndex >= 0;
      const isSinglePipelineFile =
        !isTrackedBatchFile && uploadedDocumentId === fileId && batchFileMeta.length === 0;

      if (!isTrackedBatchFile && !isSinglePipelineFile) {
        return;
      }

      if (previewingFileId === fileId) {
        setPreviewingFileId(null);
        setShowPreview(false);
      }

      if (isTrackedBatchFile) {
        const nextMeta = batchFileMeta.filter((entry) => entry.fileId !== fileId);
        const nextFiles = files.filter((_, index) => index !== metaIndex);

        setFiles(nextFiles);
        setBatchFileMeta(nextMeta);
        setBatchStatus((current) => (current ? removeFileFromBatchStatus(current, fileId) : null));
        setMappedFileIds((current) => current.filter((id) => id !== fileId));
        setStructureConfirmedFileIds((current) => current.filter((id) => id !== fileId));
        setFileRowCounts((current) => {
          const next = { ...current };
          delete next[fileId];
          return next;
        });
        setFileTableCounts((current) => {
          const next = { ...current };
          delete next[fileId];
          return next;
        });
        setFileRegionRowCounts((current) => {
          const next = { ...current };
          delete next[fileId];
          return next;
        });
        setPipelineDqxByFileId((current) => {
          const next = { ...current };
          delete next[fileId];
          return next;
        });
        dqxPollAbortByFileRef.current.get(fileId)?.abort();
        dqxPollAbortByFileRef.current.delete(fileId);
        invalidateFilePreviewCache(fileId);
        unregisterBatchFile(fileId);
        delete mappedFileSnapshotsRef.current[fileId];

        if (uploadedDocumentId === fileId) {
          setUploadedDocumentId("");
          setUploadedFileSchemaId("");
          setUploadedFileReportType(null);
          setUploadedFileAllowedReportTypes([]);
          setPreviewFileName("");
          setWorksheets([]);
          setRequiresReview(false);
          setReviewReason(null);
          setGlobalConfidence(null);
          setRegionReportTypes({});
          setMappingSelection({});
        }

        if (nextMeta.length === 0) {
          setBatchId(null);
          setBatchStartedAt(null);
          setIsPollingBatch(false);
          if (nextFiles.length === 0) {
            resetBatchState();
          }
        }
        return;
      }

      setFiles([]);
      resetPreviewState();
      resetBatchState();
      setSingleFileExtractionCategory(null);
      setIsUploading(false);
      setIsTriggeringIngestion(false);
    },
    [
      batchFileMeta,
      files,
      isBatchMode,
      previewingFileId,
      resetBatchState,
      resetPreviewState,
      resolveFileSchemaId,
      track,
      uploadedDocumentId,
    ]
  );

  const deleteFileFromBackend = useCallback(
    (fileId: string) => {
      void deleteUploadedFile(fileId).catch((err) => {
        trackError(err, { context: "pipeline_file_delete", fileId });
        setFlowError(
          `File removed from the workspace, but server-side deletion failed: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      });
    },
    [trackError]
  );

  const handleRequestRemovePipelineFile = useCallback(
    (fileId: string) => {
      const placeholder = uploadingFiles.find((entry) => (entry.fileId ?? entry.key) === fileId);
      const fileName =
        placeholder?.fileName ??
        batchFileMeta.find((entry) => entry.fileId === fileId)?.fileName ??
        (fileId === uploadedDocumentId ? previewFileName || file?.name : undefined) ??
        fileId;
      const stage = placeholder
        ? "upload"
        : getPipelineFileStage(
            mappedFileIds.includes(fileId),
            structureConfirmedFileIds.includes(fileId),
            getPipelineFileDqxState(pipelineDqxByFileId, fileId)
          );
      setDeleteFileTarget({ fileId, fileName, stage });
    },
    [
      batchFileMeta,
      file,
      mappedFileIds,
      pipelineDqxByFileId,
      previewFileName,
      structureConfirmedFileIds,
      uploadedDocumentId,
      uploadingFiles,
    ]
  );

  const handleConfirmDeleteFile = useCallback(() => {
    const target = deleteFileTarget;
    setDeleteFileTarget(null);
    if (!target) return;

    track("pipeline_file_delete_confirmed", { fileId: target.fileId, stage: target.stage });

    const placeholder = uploadingFiles.find(
      (entry) => (entry.fileId ?? entry.key) === target.fileId
    );
    if (placeholder) {
      const record = removedUploadsRef.current.get(placeholder.key) ?? { deleteRequested: false };
      if (placeholder.fileId) {
        record.deleteRequested = true;
        deleteFileFromBackend(placeholder.fileId);
      }
      removedUploadsRef.current.set(placeholder.key, record);
      setUploadingFiles((current) => current.filter((entry) => entry.key !== placeholder.key));
      return;
    }

    handleRemovePipelineFile(target.fileId);
    if (target.stage === "published") {
      // Gold output is never deleted from the board; dismissing only drops the
      // queue rows so the card stays gone across board restores.
      void dismissPublishedFile(target.fileId).catch((err) => {
        trackError(err, { context: "pipeline_file_dismiss", fileId: target.fileId });
      });
      return;
    }
    deleteFileFromBackend(target.fileId);
  }, [
    deleteFileFromBackend,
    deleteFileTarget,
    handleRemovePipelineFile,
    track,
    trackError,
    uploadingFiles,
  ]);

  const ingestUploadedFile = useCallback(
    async (
      upload: {
        file: File;
        documentId: string;
        schemaId: string;
        schemaName?: string | null;
        reportType?: string | null;
        allowedReportTypes?: string[];
        providerId?: string | null;
      },
      dropBatchId: string
    ) => {
      const meta = buildPipelineFileMetaFromUpload({
        ...upload,
        schemaName: humanSchemaName(upload.schemaId, upload.schemaName, pickerSchemaNameById),
        uploadedBy: currentUser?.userId ?? null,
      });
      registerBatchFiles([{ fileId: upload.documentId, file: upload.file }]);
      setBatchFileMeta((current) =>
        current.some((entry) => entry.fileId === meta.fileId) ? current : [...current, meta]
      );
      const starting = createStartingBatchStatus([meta], dropBatchId);
      setBatchStatus((current) => {
        const next = mergeBatchExtractionStatuses(current, starting, {
          primaryBatchId: dropBatchId,
          replaceExistingFileIds: true,
        });
        batchStatusRef.current = next;
        return next;
      });
      setWorkflowBatchStatus((current) =>
        mergeBatchExtractionStatuses(current, starting, {
          primaryBatchId: dropBatchId,
          replaceExistingFileIds: true,
        })
      );
      setBatchId(dropBatchId);
      setBatchStartedAt((prev) => prev ?? new Date().toISOString());
      setUploadingFiles((current) =>
        current.filter(
          (item) => item.fileId !== upload.documentId && item.key !== pendingFileKey(upload.file)
        )
      );
      setSingleFileExtractionCategory(null);
      await triggerBatchExtraction([upload.documentId], { batchId: dropBatchId });
    },
    [currentUser?.userId, pickerSchemaNameById]
  );

  const ensureDropPoller = useCallback(
    (dropBatchId: string, signal: AbortSignal) => {
      if (dropPollInFlightRef.current) return dropPollInFlightRef.current;

      setIsPollingBatch(true);
      const stopTimer = startTimer();
      const promise = (async () => {
        try {
          const finalStatus = await pollBatchUntilComplete(dropBatchId, signal, {
            expectedFileCount: 1,
            keepPolling: () => dropUploadsRemainingRef.current > 0,
            getMergeBase: () => batchStatusRef.current,
            mergeOptions: { primaryBatchId: dropBatchId, replaceExistingFileIds: true },
            onWorkflowStatus: setWorkflowBatchStatus,
          });
          const merged = mergeBatchExtractionStatuses(batchStatusRef.current, finalStatus, {
            primaryBatchId: dropBatchId,
            replaceExistingFileIds: true,
          });
          setBatchStatus(merged);
          batchStatusRef.current = merged;
          track("batch_ingestion_completed", {
            batchId: dropBatchId,
            fileCount: merged.totalFiles,
            succeededCount: merged.succeededCount,
            failedCount: merged.failedCount,
            skippedCount: merged.skippedCount,
            durationMs: stopTimer(),
          });
          if (merged.failedCount > 0 && merged.succeededCount === 0) {
            setFlowError("Batch ingestion failed for all files. See queue for details.");
          }
        } catch (err) {
          if (isAbortError(err) || signal.aborted) return;
          const message = err instanceof Error ? err.message : String(err);
          setFlowError(`Batch ingestion failed: ${message}`);
          track("batch_ingestion_failed", { errorMessage: message, durationMs: stopTimer() });
          trackError(err, { context: "trigger_batch_ingestion" });
        } finally {
          dropPollInFlightRef.current = null;
          if (activeDropBatchIdRef.current === dropBatchId) {
            activeDropBatchIdRef.current = null;
          }
          if (!signal.aborted) {
            setIsPollingBatch(false);
            setIsTriggeringIngestion(false);
            setActiveWorkflowFileCount(0);
            setWorkflowBatchStatus(null);
          }
        }
      })();
      dropPollInFlightRef.current = promise;
      return promise;
    },
    [pollBatchUntilComplete, startTimer, track, trackError]
  );

  const runIngestionWorkflow = useCallback(
    async (
      documentId: string,
      sourceFile: File,
      schemaId: string,
      options?: {
        reportType?: string | null;
        allowedReportTypes?: string[];
        schemaName?: string | null;
      }
    ) => {
      setIsTriggeringIngestion(true);
      setFlowError("");
      setIngestionStatusLabel(null);
      setSingleFileExtractionCategory("running");
      const schemaName =
        humanSchemaName(schemaId, options?.schemaName, pickerSchemaNameById) ??
        schemas.find((s) => s.id === schemaId)?.name ??
        schemaId;
      const needsReportType = schemaRequiresReportType(schemaName);
      const knownReportType = options?.reportType ?? uploadedFileReportType;
      const knownAllowedReportTypes = options?.allowedReportTypes ?? uploadedFileAllowedReportTypes;
      track("ingestion_trigger_started", {
        fileName: sourceFile.name,
        documentId,
        schemaId,
      });
      const stopTimer = startTimer();

      if (knownReportType && knownReportType !== "unknown") {
        setReportType(knownReportType);
        setClassificationResult({
          detectedType: knownReportType,
          allowedReportTypes:
            knownAllowedReportTypes && knownAllowedReportTypes.length > 0
              ? knownAllowedReportTypes
              : [knownReportType],
        });
      }

      const classifyPromise =
        needsReportType && !(knownReportType && knownReportType !== "unknown")
          ? (() => {
              setClassifying(true);
              return classifyFile(sourceFile, schemaId)
                .then((result) => {
                  setClassificationResult({
                    detectedType: result.detectedType,
                    allowedReportTypes: result.allowedReportTypes,
                  });
                })
                .catch((err) => {
                  setClassificationResult({
                    detectedType: null,
                    allowedReportTypes: [],
                  });
                  trackError(err, { context: "file_classify", schemaId });
                });
            })()
          : Promise.resolve();

      try {
        const triggerResult = await triggerExtractionWorkflow(documentId);
        setIngestionTriggerSkipped(!triggerResult.triggered && !!triggerResult.skipReason);
        if (triggerResult.triggered && triggerResult.runId !== null) {
          const maxPollAttempts = 120;
          let completed = false;
          for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
            const status = await fetchExtractionWorkflowStatus(documentId, triggerResult.runId);
            setIngestionStatusLabel(formatExtractionRunStatusLabel(status.lifeCycleState));
            const lifeCycleState = status.lifeCycleState.toUpperCase();
            if (lifeCycleState === "TERMINATED") {
              const resultState = (status.resultState || "").toUpperCase();
              if (resultState !== "SUCCESS") {
                throw new Error(
                  status.failedTaskMessage || status.stateMessage || "Extraction workflow failed"
                );
              }
              completed = true;
              break;
            }
            if (lifeCycleState === "INTERNAL_ERROR" || lifeCycleState === "SKIPPED") {
              throw new Error(status.stateMessage || "Extraction workflow did not complete");
            }
            await new Promise((resolve) => setTimeout(resolve, BATCH_POLL_INTERVAL_MS));
          }
          if (!completed) {
            throw new Error("Extraction polling timed out");
          }
        } else if (triggerResult.triggered && triggerResult.batchId) {
          // Inline extraction now runs server-side in the background; wait for
          // the single-file batch to reach a terminal state before previewing.
          setIngestionStatusLabel("Processing locally…");
          const maxPollAttempts = 120;
          let completed = false;
          for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
            const status = await fetchBatchExtractionStatus(triggerResult.batchId);
            if (!isBatchIngestionActive(status)) {
              const item = status.items.find((entry) => entry.fileId === documentId);
              if (item?.statusCategory === "failed" || item?.statusCategory === "cancelled") {
                throw new Error(
                  item.statusError ??
                    item.stateMessage ??
                    item.triggerError ??
                    "Extraction workflow failed"
                );
              }
              completed = true;
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, BATCH_POLL_INTERVAL_MS));
          }
          if (!completed) {
            throw new Error("Extraction polling timed out");
          }
        }

        const preview = await previewExtraction(documentId);
        await classifyPromise;
        const durationMs = stopTimer();
        applyPreviewWorksheets(preview.worksheets, undefined, knownReportType);
        setRequiresReview(preview.requiresReview);
        setReviewReason(preview.reviewReason ?? null);
        setReviewThreshold(preview.reviewThreshold);
        setGlobalConfidence(preview.globalConfidence);
        recordFileConfidence(documentId, preview.globalConfidence, preview.requiresReview);
        setProcessingDurationMs(preview.processingDurationMs ?? null);
        setExecutionMode(preview.executionMode ?? null);
        if (preview.ingestionSkipped) {
          setIngestionTriggerSkipped(true);
        }
        setPreviewFileName(sourceFile.name);
        setSingleFileExtractionCategory("succeeded");

        const sessionFile = {
          fileId: documentId,
          fileName: sourceFile.name,
          schemaId,
          providerId: sessionProviderIdRef.current,
          uploadedBy: currentUser?.userId ?? null,
        };
        const succeededStatus = createSingleFileBatchStatus(sessionFile, "succeeded");
        registerBatchFiles([{ fileId: documentId, file: sourceFile }]);
        const meta = buildPipelineFileMetaFromUpload({
          file: sourceFile,
          documentId,
          schemaId,
          schemaName: humanSchemaName(schemaId, options?.schemaName, pickerSchemaNameById),
          reportType: knownReportType,
          allowedReportTypes: knownAllowedReportTypes,
          providerId: sessionProviderIdRef.current,
          uploadedBy: currentUser?.userId ?? null,
        });
        setBatchFileMeta([meta]);
        setBatchId(succeededStatus.batchId);
        setBatchStatus(succeededStatus);
        setBatchStartedAt((prev) => prev ?? new Date().toISOString());
        setShowPreview(true);

        track("ingestion_preview_ready", {
          documentId,
          worksheetCount: preview.worksheets.length,
          durationMs,
          ...(preview.processingDurationMs != null
            ? { processingDurationMs: preview.processingDurationMs }
            : {}),
        });
      } catch (err) {
        const durationMs = stopTimer();
        const message = err instanceof Error ? err.message : String(err);
        setSingleFileExtractionCategory("failed");
        setFlowError(`Ingestion failed: ${message}`);
        track("ingestion_trigger_failed", {
          fileName: sourceFile.name,
          documentId,
          errorMessage: message,
          durationMs,
        });
        trackError(err, { context: "trigger_ingestion" });
      } finally {
        setClassifying(false);
        setIsTriggeringIngestion(false);
        setIngestionStatusLabel(null);
      }
    },
    [
      schemas,
      pickerSchemaNameById,
      setClassifying,
      setClassificationResult,
      setReportType,
      startTimer,
      track,
      trackError,
      applyPreviewWorksheets,
      uploadedFileReportType,
      uploadedFileAllowedReportTypes,
      currentUser?.userId,
      recordFileConfidence,
    ]
  );

  const handleBeginConversion = useCallback(
    async (entriesOverride?: PendingPipelineUpload[]) => {
      const toUpload = entriesOverride ?? pendingAddEntries;
      if (!entriesOverride && !addFilesProviderId && !sessionProviderIdRef.current) {
        setFlowError("Select a provider first.");
        return;
      }
      // Prefer dialog selection for this batch; handoff sets the ref before calling.
      if (addFilesProviderId) {
        sessionProviderIdRef.current = addFilesProviderId;
      }
      if (!canSubmitPendingPipelineUploads(toUpload, schemaNameById, schemaRequiresReportType)) {
        return;
      }

      const currentPipelineCount =
        batchFileMeta.length > 0 ? batchFileMeta.length : uploadedDocumentId ? 1 : 0;
      const pipelineActive = currentPipelineCount > 0;
      const useIncrementalIngest = pipelineActive || toUpload.length > 1;

      const reusePoller = Boolean(dropPollInFlightRef.current && batchAbortRef.current);
      const abortController =
        reusePoller && batchAbortRef.current ? batchAbortRef.current : new AbortController();
      if (!reusePoller) {
        batchAbortRef.current = abortController;
      }

      setActiveWorkflowFileCount((prev) =>
        reusePoller ? prev + toUpload.length : toUpload.length
      );
      setUploadedWorkflowFileCount(0);
      setIsUploading(true);
      setFlowError("");
      setAddFilesDialogOpen(false);
      setPendingAddEntries([]);
      setUploadingFiles((current) => [
        ...(reusePoller ? current : []),
        ...toUpload.map((entry) =>
          toUploadingPipelineFile(
            entry,
            pickerSchemaNameById,
            sessionProviderIdRef.current,
            currentUser?.userId ?? null
          )
        ),
      ]);

      if (!reusePoller) {
        removedUploadsRef.current = new Map();
      }

      if (useIncrementalIngest) {
        if (!pipelineActive) {
          resetBatchState();
          setActiveWorkflowFileCount(toUpload.length);
          setUploadedWorkflowFileCount(0);
          setUploadingFiles(
            toUpload.map((entry) =>
              toUploadingPipelineFile(
                entry,
                pickerSchemaNameById,
                sessionProviderIdRef.current,
                currentUser?.userId ?? null
              )
            )
          );
          setFiles(toUpload.map((entry) => entry.file));
          setSingleFileExtractionCategory(null);
          batchAbortRef.current = abortController;
        } else {
          if (batchFileMeta.length === 0 && uploadedDocumentId) {
            const singleName = previewFileName || files[0]?.name || uploadedDocumentId;
            const singleMeta: PipelineFileMeta = {
              fileId: uploadedDocumentId,
              fileName: singleName,
              schemaId: uploadedFileSchemaId || defaultSchemaId,
              schemaName: humanSchemaName(
                uploadedFileSchemaId || defaultSchemaId,
                null,
                pickerSchemaNameById
              ),
              reportType: uploadedFileReportType,
              allowedReportTypes: uploadedFileAllowedReportTypes,
              providerId: sessionProviderIdRef.current,
              uploadedBy: currentUser?.userId ?? null,
            };
            setBatchFileMeta([singleMeta]);
            if (!batchStatusRef.current) {
              const seeded = createSingleFileBatchStatus(
                {
                  fileId: uploadedDocumentId,
                  fileName: singleName,
                  schemaId: singleMeta.schemaId,
                },
                singleFileExtractionCategory ?? "succeeded"
              );
              setBatchStatus(seeded);
              batchStatusRef.current = seeded;
            }
            setUploadedDocumentId("");
          }
          setFiles((current) => {
            const existingKeys = new Set(current.map(pendingFileKey));
            const extra = toUpload
              .map((entry) => entry.file)
              .filter((file) => !existingKeys.has(pendingFileKey(file)));
            return extra.length > 0 ? [...current, ...extra] : current;
          });
        }

        const dropBatchId =
          reusePoller && activeDropBatchIdRef.current
            ? activeDropBatchIdRef.current
            : newDropBatchId();
        activeDropBatchIdRef.current = dropBatchId;
        dropUploadsRemainingRef.current = reusePoller
          ? dropUploadsRemainingRef.current + toUpload.length
          : toUpload.length;

        track("upload_started", {
          fileCount: toUpload.length,
          batchMode: toUpload.length > 1,
          append: pipelineActive,
        });
        const stopTimer = startTimer();
        const entryByKey = new Map(toUpload.map((entry) => [pendingFileKey(entry.file), entry]));

        try {
          await uploadPendingEntries(
            toUpload,
            abortController.signal,
            async (completed, _total, uploaded) => {
              setUploadedWorkflowFileCount(completed);
              const entry = entryByKey.get(uploaded.key);
              const removed = removedUploadsRef.current.get(uploaded.key);
              if (!entry || removed) {
                if (removed && !removed.deleteRequested) {
                  removed.deleteRequested = true;
                  deleteFileFromBackend(uploaded.documentId);
                }
                dropUploadsRemainingRef.current = Math.max(0, dropUploadsRemainingRef.current - 1);
                setUploadingFiles((current) => current.filter((item) => item.key !== uploaded.key));
                return;
              }
              try {
                await ingestUploadedFile(
                  {
                    file: entry.file,
                    documentId: uploaded.documentId,
                    schemaId: entry.schemaId,
                    schemaName: humanSchemaName(entry.schemaId, null, pickerSchemaNameById),
                    reportType: entry.reportType ?? null,
                    allowedReportTypes: entry.allowedReportTypes,
                    providerId: sessionProviderIdRef.current,
                  },
                  dropBatchId
                );
                void ensureDropPoller(dropBatchId, abortController.signal);
              } catch (err) {
                trackError(err, {
                  context: "trigger_file_ingestion",
                  documentId: uploaded.documentId,
                });
                setFlowError(
                  `Extraction trigger failed for ${entry.file.name}: ${
                    err instanceof Error ? err.message : String(err)
                  }`
                );
              } finally {
                dropUploadsRemainingRef.current = Math.max(0, dropUploadsRemainingRef.current - 1);
              }
            },
            sessionProviderIdRef.current
          );
          if (abortController.signal.aborted) return;
          track("upload_complete", {
            fileCount: toUpload.length,
            durationMs: stopTimer(),
            batchMode: toUpload.length > 1,
            append: pipelineActive,
          });
          setIsUploading(false);
          setUploadedWorkflowFileCount(0);
          setUploadingFiles([]);
          if (dropPollInFlightRef.current) {
            await dropPollInFlightRef.current;
          }
        } catch (err) {
          if (isAbortError(err) || abortController.signal.aborted) {
            return;
          }
          const durationMs = stopTimer();
          setFlowError(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
          trackError(err, {
            context: "file_upload",
            fileCount: toUpload.length,
            durationMs,
            append: pipelineActive,
          });
        } finally {
          if (!abortController.signal.aborted && !dropPollInFlightRef.current) {
            batchAbortRef.current = null;
          }
          setIsUploading(false);
          setUploadedWorkflowFileCount(0);
          setUploadingFiles([]);
        }
        return;
      }

      resetBatchState();
      batchAbortRef.current = abortController;
      setActiveWorkflowFileCount(toUpload.length);
      setUploadedWorkflowFileCount(0);
      setUploadingFiles(
        toUpload.map((entry) =>
          toUploadingPipelineFile(
            entry,
            pickerSchemaNameById,
            sessionProviderIdRef.current,
            currentUser?.userId ?? null
          )
        )
      );
      setFiles(toUpload.map((entry) => entry.file));
      setSingleFileExtractionCategory(null);

      track("upload_started", {
        fileCount: toUpload.length,
        batchMode: false,
      });

      const stopTimer = startTimer();
      try {
        const singleEntry = toUpload[0];
        if (!singleEntry) return;
        const confirmation = await uploadFile(
          singleEntry.file,
          singleEntry.schemaId,
          abortController.signal,
          sessionProviderIdRef.current
        );
        const removedRecord = removedUploadsRef.current.get(pendingFileKey(singleEntry.file));
        if (removedRecord) {
          if (!removedRecord.deleteRequested) {
            removedRecord.deleteRequested = true;
            deleteFileFromBackend(confirmation.documentId);
          }
          setFiles([]);
          return;
        }
        setUploadedWorkflowFileCount(1);
        const durationMs = stopTimer();
        setUploadingFiles([]);
        setUploadedDocumentId(confirmation.documentId);
        setUploadedFileSchemaId(singleEntry.schemaId);
        setUploadedFileReportType(singleEntry.reportType ?? null);
        setUploadedFileAllowedReportTypes(singleEntry.allowedReportTypes ?? []);
        setPreviewFileName(singleEntry.file.name);
        setSingleFileExtractionCategory("pending");
        registerBatchFiles([{ fileId: confirmation.documentId, file: singleEntry.file }]);
        track("upload_complete", {
          fileExtension: singleEntry.file.name.split(".").pop()?.toLowerCase() ?? "unknown",
          schemaId: singleEntry.schemaId,
          documentId: confirmation.documentId,
          durationMs,
        });
        await runIngestionWorkflow(
          confirmation.documentId,
          singleEntry.file,
          singleEntry.schemaId,
          {
            reportType: singleEntry.reportType ?? null,
            allowedReportTypes: singleEntry.allowedReportTypes ?? [],
            schemaName: humanSchemaName(singleEntry.schemaId, null, pickerSchemaNameById),
          }
        );
      } catch (err) {
        if (isAbortError(err) || abortController.signal.aborted) {
          return;
        }
        const durationMs = stopTimer();
        setFlowError(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
        trackError(err, {
          context: "file_upload",
          fileCount: toUpload.length,
          durationMs,
        });
      } finally {
        if (!abortController.signal.aborted) {
          batchAbortRef.current = null;
        }
        setIsUploading(false);
        setUploadedWorkflowFileCount(0);
        setUploadingFiles([]);
      }
    },
    [
      addFilesProviderId,
      batchFileMeta.length,
      currentUser?.userId,
      defaultSchemaId,
      deleteFileFromBackend,
      ensureDropPoller,
      files,
      ingestUploadedFile,
      pendingAddEntries,
      previewFileName,
      resetBatchState,
      runIngestionWorkflow,
      schemaNameById,
      pickerSchemaNameById,
      singleFileExtractionCategory,
      startTimer,
      track,
      trackError,
      uploadedDocumentId,
      uploadedFileAllowedReportTypes,
      uploadedFileReportType,
      uploadedFileSchemaId,
    ]
  );

  // Home → /pipeline handoff: start the same upload path as Add files → Upload & extract.
  const handleBeginConversionRef = useRef(handleBeginConversion);
  handleBeginConversionRef.current = handleBeginConversion;
  const homeHandoffConsumedRef = useRef(false);
  useEffect(() => {
    if (homeHandoffConsumedRef.current) return;
    const handoff = takePipelineUploadHandoff();
    if (!handoff || handoff.entries.length === 0) return;
    homeHandoffConsumedRef.current = true;
    sessionProviderIdRef.current = handoff.providerId ?? null;
    void handleBeginConversionRef.current(handoff.entries);
  }, []);

  const handleTriggerIngestion = useCallback(async () => {
    if (!file || !uploadedDocumentId) return;
    await runIngestionWorkflow(uploadedDocumentId, file, uploadedFileSchemaId || defaultSchemaId);
  }, [defaultSchemaId, file, runIngestionWorkflow, uploadedDocumentId, uploadedFileSchemaId]);

  const handleBatchPreview = useCallback(
    async (fileId: string) => {
      const meta = batchFileMeta.find((entry) => entry.fileId === fileId);
      const sourceFile = await resolveBatchSourceFile(fileId, files, file, batchFileMeta);
      const displayName = meta?.fileName ?? previewFileName ?? file?.name ?? fileId;
      seedReportTypeFromMeta(fileId);
      setFlowError("");
      setPreviewingFileId(fileId);
      setPreviewFileName(displayName);
      setWorksheets([]);
      setRequiresReview(false);
      setReviewReason(null);
      setGlobalConfidence(null);
      setProcessingDurationMs(null);
      setIngestionTriggerSkipped(false);
      setExecutionMode(null);
      setIngestionStatusLabel(`Loading preview for ${displayName}…`);
      setShowPreview(true);
      setIsTriggeringIngestion(true);
      try {
        const preview = await previewExtraction(fileId);
        setUploadedDocumentId(fileId);
        setRequiresReview(preview.requiresReview);
        setReviewReason(preview.reviewReason ?? null);
        setReviewThreshold(preview.reviewThreshold);
        setGlobalConfidence(preview.globalConfidence);
        recordFileConfidence(fileId, preview.globalConfidence, preview.requiresReview);
        setProcessingDurationMs(preview.processingDurationMs ?? null);
        setExecutionMode(preview.executionMode ?? null);
        setIngestionTriggerSkipped(preview.ingestionSkipped ?? false);

        if (sourceFile && !meta?.reportType) {
          const fileSchemaId = resolveFileSchemaId(fileId);
          const schemaName = schemas.find((s) => s.id === fileSchemaId)?.name ?? fileSchemaId;
          if (schemaRequiresReportType(schemaName)) {
            setClassifying(true);
            try {
              const result = await classifyFile(sourceFile, fileSchemaId);
              setClassificationResult({
                detectedType: result.detectedType,
                allowedReportTypes: result.allowedReportTypes,
              });
            } catch (err) {
              setClassificationResult({ detectedType: null, allowedReportTypes: [] });
              trackError(err, { context: "file_classify", schemaId: fileSchemaId });
            } finally {
              setClassifying(false);
            }
          }
        }

        let savedSelection: Record<string, boolean> | undefined;
        try {
          const savedRegions = await loadMappingSelection(fileId);
          savedSelection = buildMappingSelectionFromSaved(
            normalizeWorksheetPreviewMetadata(preview.worksheets),
            savedRegions
          );
        } catch {
          savedSelection = undefined;
        }

        applyPreviewWorksheets(preview.worksheets, savedSelection, meta?.reportType ?? null);
        const normalizedWorksheets = normalizeWorksheetPreviewMetadata(preview.worksheets);
        recordFilePreviewMetrics(
          fileId,
          normalizedWorksheets,
          savedSelection ?? buildDefaultMappingSelection(normalizedWorksheets)
        );
      } catch (err) {
        setShowPreview(false);
        setFlowError(`Preview failed: ${err instanceof Error ? err.message : String(err)}`);
        trackError(err, { context: "batch_preview", fileId });
      } finally {
        setIsTriggeringIngestion(false);
        setIngestionStatusLabel(null);
        setPreviewingFileId(null);
      }
    },
    [
      batchFileMeta,
      file,
      files,
      previewFileName,
      resolveFileSchemaId,
      schemas,
      seedReportTypeFromMeta,
      setClassificationResult,
      setClassifying,
      trackError,
      applyPreviewWorksheets,
      recordFilePreviewMetrics,
      recordFileConfidence,
    ]
  );

  // Rebuild the board from server-derived state — the single source of truth
  // on load: file + queue rows give extraction status, selection/snapshot
  // files give the confirmed/mapped stages, DQX runs give
  // validate/resolve/published.
  const syncBoardFromBoardState = useCallback(async (): Promise<BatchIngestionSession | null> => {
    try {
      const boardFiles = await fetchBoardState(boardStateFilters);
      return buildSessionFromBoardFiles(boardFiles);
    } catch (err) {
      trackError(err, { context: "board_state_sync" });
      return null;
    }
  }, [boardStateFilters, trackError]);

  const applyBoardSession = useCallback((session: BatchIngestionSession) => {
    setBatchId(session.batchId);
    setBatchFileMeta((current) => mergePipelineFileMetaSchemaNames(session.files, current));
    setMappedFileIds(session.mappedFileIds ?? []);
    setStructureConfirmedFileIds(session.structureConfirmedFileIds ?? []);
    setFileRowCounts(session.fileRowCounts ?? {});
    setFileTableCounts(session.fileTableCounts ?? {});
    setFileRegionRowCounts(session.fileRegionRowCounts ?? {});
    setPipelineDqxByFileId(session.fileDqxState ?? {});
    setBatchStartedAt(session.startedAt);
    if (session.completedBatchStatus) {
      setBatchStatus(session.completedBatchStatus);
    }
  }, []);

  const resumeBatchSession = useCallback(async () => {
    const session = await syncBoardFromBoardState();

    if (!session) {
      setIsRestoringBoard(false);
      openAddFilesDialog();
      return;
    }

    applyBoardSession(session);
    setIsRestoringBoard(false);
    const resumedDefaultSchema = session.files[0]?.schemaId ?? session.schemaId ?? defaultSchemaId;
    if (resumedDefaultSchema) {
      setDefaultSchemaId(resumedDefaultSchema);
    }

    const terminalStatus =
      session.completedBatchStatus && !isBatchIngestionActive(session.completedBatchStatus)
        ? session.completedBatchStatus
        : null;

    if (terminalStatus) {
      void prefetchPipelinePreviewMetrics(terminalStatus);
      for (const [fileId, state] of Object.entries(session.fileDqxState ?? {})) {
        if (shouldResumeDqxPolling(state) && state.runId != null) {
          const runs =
            state.runs && state.runs.length > 0
              ? state.runs.map((r) => ({
                  regionId: r.regionId,
                  runId: r.runId,
                  jobId: r.jobId,
                  message: r.message,
                  phase: r.phase,
                  rowsPassing: r.rowsPassing,
                  rowsWithIssues: r.rowsWithIssues,
                  totalRowsChecked: r.totalRowsChecked,
                  error: r.error,
                }))
              : [{ runId: state.runId, jobId: state.jobId, message: state.message }];
          if (runs.length > 0) startDqxPolling(fileId, runs);
        }
      }
      return;
    }

    setIsPollingBatch(true);
    const abortController = new AbortController();
    batchAbortRef.current = abortController;
    // The queue is keyed by batch, the board by provider scope: merge rather
    // than replace so files from other batches survive the refresh.
    const restoredStatus = session.completedBatchStatus ?? null;
    const mergeOptions = { primaryBatchId: session.batchId, replaceExistingFileIds: true };
    try {
      const status = await fetchBatchExtractionStatus(session.batchId, abortController.signal);
      const mergedStatus = mergeBatchExtractionStatuses(restoredStatus, status, mergeOptions);
      setBatchStatus(mergedStatus);

      if (isBatchIngestionActive(status)) {
        setActiveWorkflowFileCount(status.totalFiles);
        setWorkflowBatchStatus(status);
        const finalStatus = await pollBatchUntilComplete(session.batchId, abortController.signal, {
          initialStatus: status,
          expectedFileCount: session.files.length,
          mergeWith: restoredStatus,
          mergeOptions,
          onWorkflowStatus: setWorkflowBatchStatus,
        });
        const finalMerged = mergeBatchExtractionStatuses(restoredStatus, finalStatus, mergeOptions);
        setBatchStatus(finalMerged);
        void prefetchPipelinePreviewMetrics(finalMerged);
        track("batch_ingestion_resumed", {
          batchId: session.batchId,
          succeededCount: finalStatus.succeededCount,
          failedCount: finalStatus.failedCount,
        });
      } else {
        void prefetchPipelinePreviewMetrics(mergedStatus);
      }
    } catch (err) {
      if (isAbortError(err) || abortController.signal.aborted) {
        return;
      }
      trackError(err, { context: "resume_batch_session" });
    } finally {
      if (!abortController.signal.aborted) {
        batchAbortRef.current = null;
      }
      setIsPollingBatch(false);
      setActiveWorkflowFileCount(0);
      setWorkflowBatchStatus(null);
    }

    for (const [fileId, state] of Object.entries(session.fileDqxState ?? {})) {
      if (shouldResumeDqxPolling(state) && state.runId != null) {
        const runs =
          state.runs && state.runs.length > 0
            ? state.runs.map((r) => ({
                regionId: r.regionId,
                runId: r.runId,
                jobId: r.jobId,
                message: r.message,
                phase: r.phase,
                rowsPassing: r.rowsPassing,
                rowsWithIssues: r.rowsWithIssues,
                totalRowsChecked: r.totalRowsChecked,
                error: r.error,
              }))
            : [{ runId: state.runId, jobId: state.jobId, message: state.message }];
        if (runs.length > 0) startDqxPolling(fileId, runs);
      }
    }
  }, [
    applyBoardSession,
    defaultSchemaId,
    openAddFilesDialog,
    pollBatchUntilComplete,
    prefetchPipelinePreviewMetrics,
    startDqxPolling,
    syncBoardFromBoardState,
    track,
    trackError,
  ]);

  useEffect(() => {
    if (resumeAttempted.current) return;
    resumeAttempted.current = true;
    void resumeBatchSession();
  }, [resumeBatchSession]);

  // Board scope filter changes: re-fetch scoped board-state (skip the initial mount —
  // resumeBatchSession already applied the default unfiltered scope).
  useEffect(() => {
    if (!showProviderBoardFilter) return;
    if (!boardScopeFiltersReadyRef.current) {
      boardScopeFiltersReadyRef.current = true;
      return;
    }
    let cancelled = false;
    setIsRestoringBoard(true);
    // Hide the previous scope's cards immediately — otherwise they linger for
    // the whole fetch and only vanish at the end when the new scope is empty.
    setBatchFileMeta([]);
    setMappedFileIds([]);
    setBatchStatus(null);
    setPipelineDqxByFileId({});
    setFileRowCounts({});
    setFileTableCounts({});
    setFileRegionRowCounts({});
    void (async () => {
      const hydrated = await syncBoardFromBoardState();
      if (cancelled) return;
      setIsRestoringBoard(false);
      if (hydrated) {
        applyBoardSession(hydrated);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showProviderBoardFilter, boardStateFilters, syncBoardFromBoardState, applyBoardSession]);
  // Blob + extraction stay until DQ is *clean* (Published), not merely
  // job-complete. Resolve still re-applies mappings from extraction after
  // cleanses, and View DQ after a refresh needs the preview when artifacts
  // remain. Cleaning on phase===complete alone deleted them for cards still
  // in Resolve and blocked post-refresh View DQ / re-run.
  useEffect(() => {
    for (const [fileId, state] of Object.entries(pipelineDqxByFileId)) {
      if (!shouldReleaseDqxArtifacts(state)) continue;
      const cleanedKey = stageKey(fileId, "dq_complete");
      if (cleanedStagesRef.current.has(cleanedKey)) continue;
      cleanedStagesRef.current.add(cleanedKey);
      void cleanupFileArtifacts(fileId, "dq_complete").catch((err) => {
        trackError(err, { context: "cleanup_dq_complete", fileId });
      });
    }
  }, [pipelineDqxByFileId, trackError]);

  const beginPipelineAction = useCallback(
    (fileId: string, action: "mapping" | "dqx" | "confirm" | "download"): boolean => {
      if (pendingPipelineActionFileIdsRef.current.has(fileId)) return false;
      pendingPipelineActionFileIdsRef.current.add(fileId);
      setPendingPipelineActionByFileId((current) => ({ ...current, [fileId]: action }));
      return true;
    },
    []
  );

  const finishPipelineAction = useCallback((fileId: string) => {
    pendingPipelineActionFileIdsRef.current.delete(fileId);
    setPendingPipelineActionByFileId((current) => {
      if (!(fileId in current)) return current;
      const next = { ...current };
      delete next[fileId];
      return next;
    });
  }, []);

  const handleConfirmStructure = useCallback(async () => {
    if (!uploadedDocumentId) return;

    if (requiresReview) {
      setFlowError(
        reviewReason || "Ingestion confidence is below the minimum required to continue to mapping."
      );
      return;
    }

    const selectedMappingCount = countSelectedMappingWorksheets(worksheets, mappingSelection);
    if (selectedMappingCount < 1) {
      setFlowError("Select at least one region to include in mapping.");
      return;
    }

    const documentId = uploadedDocumentId;
    if (!beginPipelineAction(documentId, "confirm")) return;
    setFlowError("");
    setShowPreview(false);

    try {
      const sourceFile = await resolveBatchSourceFile(documentId, files, file, batchFileMeta);
      if (!sourceFile) {
        setFlowError(
          "Could not load the original file. Try confirming structure again or re-process the batch."
        );
        setShowPreview(true);
        return;
      }

      const fileSchemaId = resolveFileSchemaId(documentId);
      const schemaName = schemas.find((s) => s.id === fileSchemaId)?.name ?? fileSchemaId;
      const isMultiRegionPreview = selectedMappingCount > 1;
      const storedReportType =
        batchFileMeta.find((entry) => entry.fileId === documentId)?.reportType ??
        uploadedFileReportType ??
        reportType;

      await saveMappingSelection(
        documentId,
        buildMappingSelectionPayload(worksheets, mappingSelection)
      );

      confirmUpload({
        file: sourceFile,
        fileName: previewFileName || sourceFile.name,
        documentId,
        schemaId: fileSchemaId,
        schemaName,
        worksheets,
        mappingSelection,
        reportType: isMultiRegionPreview ? null : storedReportType,
        ...(isMultiRegionPreview ? { regionReportTypes } : {}),
      });

      recordFilePreviewMetrics(documentId, worksheets, mappingSelection);

      setStructureConfirmedFileIds((prev) =>
        prev.includes(documentId) ? prev : [...prev, documentId]
      );
      setSingleFileExtractionCategory("succeeded");

      const cleanedKey = stageKey(documentId, "structure_confirmed");
      if (!cleanedStagesRef.current.has(cleanedKey)) {
        cleanedStagesRef.current.add(cleanedKey);
        void cleanupFileArtifacts(documentId, "structure_confirmed").catch((err) => {
          trackError(err, { context: "cleanup_structure_confirmed", fileId: documentId });
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setFlowError(`Could not confirm structure: ${message}`);
      setShowPreview(true);
      trackError(err, { context: "confirm_structure", documentId });
    } finally {
      finishPipelineAction(documentId);
    }
  }, [
    batchFileMeta,
    beginPipelineAction,
    confirmUpload,
    file,
    files,
    finishPipelineAction,
    previewFileName,
    regionReportTypes,
    reportType,
    resolveFileSchemaId,
    uploadedFileReportType,
    schemas,
    uploadedDocumentId,
    worksheets,
    mappingSelection,
    requiresReview,
    reviewReason,
    recordFilePreviewMetrics,
    trackError,
  ]);

  const handleContinueToMapping = useCallback(
    async (fileId: string) => {
      if (!beginPipelineAction(fileId, "mapping")) return;
      setFlowError("");
      try {
        const ctx = await loadPipelineFilePreviewContext(buildLoadContextParams(fileId));
        confirmPipelineFileContext(ctx);
        setMappingDialogOpen(true);
      } catch (err) {
        setFlowError(
          `Could not continue to mapping: ${err instanceof Error ? err.message : String(err)}`
        );
        trackError(err, { context: "continue_to_mapping", fileId });
      } finally {
        finishPipelineAction(fileId);
      }
    },
    [
      beginPipelineAction,
      buildLoadContextParams,
      confirmPipelineFileContext,
      finishPipelineAction,
      trackError,
    ]
  );

  const handleRunDataQuality = useCallback(
    async (fileId: string): Promise<boolean> => {
      const existing = pipelineDqxByFileId[fileId];
      if (existing?.phase === "running") return false;
      if (!beginPipelineAction(fileId, "dqx")) return false;

      setFlowError("");
      try {
        const ctx = await loadPipelineFilePreviewContext(buildLoadContextParams(fileId));
        applyPipelineFileContext(ctx);

        let savedMappings = mappedFileSnapshotsRef.current[fileId];
        if (!savedMappings?.length) {
          // Board restored in a fresh tab: the snapshot lives server-side.
          const remote = await fetchMappingSnapshot(fileId);
          if (remote?.length) {
            mappedFileSnapshotsRef.current[fileId] = remote;
            savedMappings = remote;
          }
        }
        if (!savedMappings?.length) {
          setFlowError("No saved mappings found for this file. Complete mapping first.");
          return false;
        }

        const schemaFields = await loadSchemaFieldsForMappings(ctx.schemaId);
        const { runs } = await applyMappingsPerRegionWithDqx({
          fileId,
          schemaId: ctx.schemaId,
          reportType: ctx.reportType,
          savedMappings,
          schemaFields,
        });

        track("pipeline_dqx_triggered", {
          fileId,
          runCount: runs.length,
          schemaId: ctx.schemaId,
          multiTable: runs.length > 1,
        });
        startDqxPolling(
          fileId,
          runs.map((run) => ({
            regionId: run.regionId,
            runId: run.runId,
            jobId: run.jobId,
            message: run.message,
          }))
        );
        return true;
      } catch (err) {
        setFlowError(
          `Could not run data quality checks: ${err instanceof Error ? err.message : String(err)}`
        );
        trackError(err, { context: "run_data_quality", fileId });
        return false;
      } finally {
        finishPipelineAction(fileId);
      }
    },
    [
      applyPipelineFileContext,
      beginPipelineAction,
      buildLoadContextParams,
      finishPipelineAction,
      loadSchemaFieldsForMappings,
      pipelineDqxByFileId,
      startDqxPolling,
      track,
      trackError,
    ]
  );

  const openDqxResultsPage = useCallback(
    async (fileId: string, trackEvent: string) => {
      setFlowError("");
      const dqxState = pipelineDqxByFileId[fileId];
      const dialogRuns = listPipelineDqxDialogRuns(dqxState);
      const issuesIndex = dialogRuns.findIndex((run) => (run.rowsWithIssues ?? 0) > 0);
      const startIndex = issuesIndex >= 0 ? issuesIndex : 0;
      const firstRun = selectPipelineDqxDialogRun(dialogRuns, startIndex);
      if (!firstRun?.jobId) {
        setFlowError("DQ results are not available yet.");
        return;
      }

      // Best-effort upload-context hydrate for cleanse/rerun UI. Never block
      // View DQ on extraction preview: after refresh the in-memory preview
      // cache is gone, and for Published cards artifacts may already be
      // released. DataQualityView loads quarantine via jobId + boardFileId.
      try {
        await hydrateDqxNavigationFromSession({
          fileId,
          batchFileMeta,
          files,
          file,
          schemas,
          savedMappingsByFileId: mappedFileSnapshotsRef.current,
          confirmUpload,
          saveMappings,
        });
      } catch (err) {
        trackError(err, { context: "dqx_results_context_unavailable", fileId });
      }

      setDqxRunInfo({
        runId: firstRun.runId,
        jobId: firstRun.jobId,
        message: firstRun.message ?? "",
      });
      setDqxDialogFileId(fileId);
      setDqxDialogInitialTableIndex(startIndex);
      setDqxDialogAutoRetrieve(true);
      setDqxDialogOpen(true);
      track(trackEvent, { fileId, jobId: firstRun.jobId, tableIndex: startIndex });
    },
    [
      batchFileMeta,
      confirmUpload,
      file,
      files,
      pipelineDqxByFileId,
      saveMappings,
      schemas,
      setDqxRunInfo,
      track,
      trackError,
    ]
  );

  const handleViewDqxResults = useCallback(
    async (fileId: string) => {
      try {
        await openDqxResultsPage(fileId, "pipeline_view_dqx_results");
      } catch (err) {
        setFlowError(
          `Could not open DQ results: ${err instanceof Error ? err.message : String(err)}`
        );
        trackError(err, { context: "view_dqx_results", fileId });
      }
    },
    [openDqxResultsPage, trackError]
  );

  // Dialogs report their outcomes via events (onFileMapped, onDqxRerunStarted),
  // so closing them needs no state re-sync.
  const closeMappingDialog = useCallback(() => {
    setMappingDialogOpen(false);
  }, []);

  const openDqxDialogFromMapping = useCallback(() => {
    setMappingDialogOpen(false);
    if (uploadDocumentId?.trim()) {
      setDqxDialogFileId(uploadDocumentId.trim());
    }
    setDqxDialogInitialTableIndex(0);
    setDqxDialogAutoRetrieve(true);
    setDqxDialogOpen(true);
  }, [uploadDocumentId]);

  const closeDqxDialog = useCallback(() => {
    setDqxDialogOpen(false);
  }, []);

  /**
   * A file's mapping set was completed and applied inside the mapping dialog.
   * The board owns the resulting stage transition (and DQ polling when the
   * final apply also triggered a run).
   */
  const handleFileMapped = useCallback(
    (event: MappingWorkbenchFileMappedEvent) => {
      mappedFileSnapshotsRef.current[event.fileId] = event.savedMappings;
      setMappedFileIds((current) =>
        current.includes(event.fileId) ? current : [...current, event.fileId]
      );
      const priorRuns = pipelineDqxRunsToWorkbenchRuns(
        listPipelineDqxDialogRuns(pipelineDqxByFileId[event.fileId])
      );
      const eventRuns = event.dqxRuns?.filter((run) => run.runId != null && run.jobId) ?? [];
      const mergedRuns = mergeMappingWorkbenchDqxRuns(priorRuns, eventRuns);
      if (mergedRuns.length > 0) {
        startDqxPolling(
          event.fileId,
          mergedRuns.map((run) => ({
            regionId: run.regionId,
            runId: run.runId,
            jobId: run.jobId,
            message: run.message,
            phase: "running" as const,
            sourceRowIndexStart: run.sourceRowIndexStart ?? null,
          }))
        );
      } else if (event.dqx) {
        startDqxPolling(event.fileId, [
          {
            regionId: event.dqx.regionId ?? null,
            runId: event.dqx.runId,
            jobId: event.dqx.jobId,
            message: event.dqx.message,
            phase: "running",
            sourceRowIndexStart: event.dqx.sourceRowIndexStart ?? null,
          },
        ]);
      } else {
        updatePipelineDqxState(event.fileId, IDLE_PIPELINE_DQX_STATE);
      }
    },
    [pipelineDqxByFileId, startDqxPolling, updatePipelineDqxState]
  );

  /**
   * A manual-cleanse rerun started inside the DQ dialog. The board owns this
   * transition directly — it must not depend on the shared session existing.
   */
  const handleDqxRerunStarted = useCallback(
    (rerun: DqxRerunStartedEvent) => {
      const prior = pipelineDqxByFileId[rerun.fileId];
      const priorRuns = listPipelineDqxDialogRuns(prior);
      if (priorRuns.length > 1) {
        let replaceIndex = priorRuns.length - 1;
        if (rerun.regionFilterKey) {
          const found = priorRuns.findIndex(
            (run, i) => regionFilterKeyForRun(run.regionId, i) === rerun.regionFilterKey
          );
          if (found >= 0) replaceIndex = found;
        } else if (rerun.regionId) {
          const found = priorRuns.findIndex((run) => run.regionId === rerun.regionId);
          if (found >= 0) replaceIndex = found;
        } else {
          for (let i = priorRuns.length - 1; i >= 0; i -= 1) {
            if ((priorRuns[i]!.rowsWithIssues ?? 0) > 0) {
              replaceIndex = i;
              break;
            }
          }
        }
        const active = priorRuns[replaceIndex]!;
        const nextState = replacePipelineDqxRunAtIndex(
          prior ?? IDLE_PIPELINE_DQX_STATE,
          replaceIndex,
          {
            regionId: active.regionId ?? rerun.regionId ?? null,
            runId: rerun.runId,
            jobId: rerun.jobId,
            phase: "running",
            message: null,
            rowsPassing: null,
            rowsWithIssues: null,
            totalRowsChecked: null,
            error: null,
          }
        );
        updatePipelineDqxState(rerun.fileId, nextState);
        startDqxPolling(
          rerun.fileId,
          (nextState.runs ?? []).map((run) => ({
            regionId: run.regionId,
            runId: run.runId,
            jobId: run.jobId,
            message: run.message,
            phase: run.phase,
            rowsPassing: run.rowsPassing,
            rowsWithIssues: run.rowsWithIssues,
            totalRowsChecked: run.totalRowsChecked,
            error: run.error,
          }))
        );
      } else {
        updatePipelineDqxState(rerun.fileId, {
          phase: "running",
          runId: rerun.runId,
          jobId: rerun.jobId,
          message: null,
          rowsPassing: prior?.rowsPassing ?? null,
          rowsWithIssues: prior?.rowsWithIssues ?? null,
          totalRowsChecked: prior?.totalRowsChecked ?? null,
        });
        startDqxPolling(rerun.fileId, [{ runId: rerun.runId, jobId: rerun.jobId }]);
      }
      closeDqxDialog();
    },
    [closeDqxDialog, pipelineDqxByFileId, startDqxPolling, updatePipelineDqxState]
  );

  const buildCurrentBulkTargets = useCallback(() => {
    const meta: PipelineFileMeta[] =
      batchFileMeta.length > 0
        ? batchFileMeta
        : uploadedDocumentId
          ? [
              {
                fileId: uploadedDocumentId,
                fileName: previewFileName || file?.name || uploadedDocumentId,
                schemaId: uploadedFileSchemaId || defaultSchemaId,
                schemaName: humanSchemaName(
                  uploadedFileSchemaId || defaultSchemaId,
                  null,
                  pickerSchemaNameById
                ),
                reportType: uploadedFileReportType,
                allowedReportTypes: uploadedFileAllowedReportTypes,
                providerId: sessionProviderIdRef.current,
                uploadedBy: currentUser?.userId ?? null,
              },
            ]
          : [];
    const model = buildPipelineBoardModel({
      batchStatus,
      fileMeta: meta,
      schemaNameByFileId: buildSchemaNameByFileId(meta, pickerSchemaNameById),
      mappedFileIds,
      structureConfirmedFileIds,
      previewingFileId,
      rowCountByFileId: fileRowCounts,
      tableCountByFileId: fileTableCounts,
      globalConfidenceByFileId: fileGlobalConfidence,
      requiresReviewByFileId: fileRequiresReview,
      dqxStateByFileId: pipelineDqxByFileId,
      currentUserId: currentUser?.userId ?? null,
      currentUserEmail: currentUser?.userEmail ?? null,
      singleFile:
        !isBatchMode && uploadedDocumentId && files.length === 1
          ? {
              fileId: uploadedDocumentId,
              fileName: previewFileName || file!.name,
              category:
                singleFileExtractionCategory ??
                (isTriggeringIngestion || isUploading
                  ? ("running" as const)
                  : ("pending" as const)),
              stateMessage: ingestionStatusLabel,
              isExtracting: isTriggeringIngestion || isUploading,
              uploadedBy: currentUser?.userId ?? null,
            }
          : null,
    });
    return { meta, targets: getPipelineBulkActionTargets(model.cards) };
  }, [
    batchFileMeta,
    batchStatus,
    currentUser?.userEmail,
    currentUser?.userId,
    defaultSchemaId,
    file,
    fileRowCounts,
    fileTableCounts,
    fileGlobalConfidence,
    fileRequiresReview,
    files.length,
    ingestionStatusLabel,
    isBatchMode,
    isTriggeringIngestion,
    isUploading,
    mappedFileIds,
    pipelineDqxByFileId,
    previewFileName,
    previewingFileId,
    pickerSchemaNameById,
    singleFileExtractionCategory,
    structureConfirmedFileIds,
    uploadedDocumentId,
    uploadedFileAllowedReportTypes,
    uploadedFileReportType,
    uploadedFileSchemaId,
  ]);

  const startDqForIds = useCallback(
    async (ids: string[], eventName: string) => {
      if (bulkProgress || ids.length === 0) return;

      setFlowError("");
      setFlowInfo("");
      const total = ids.length;
      let completed = 0;
      const failures: string[] = [];
      setBulkProgress({ label: `Starting DQ 0 of ${total}…` });

      try {
        await runWithConcurrencyLimit(ids, BULK_ACTION_CONCURRENCY, async (fileId) => {
          const ok = await handleRunDataQuality(fileId);
          if (!ok) failures.push(fileId);
          completed += 1;
          setBulkProgress({ label: `Starting DQ ${completed} of ${total}…` });
        });

        if (failures.length > 0) {
          setFlowError(
            `${total - failures.length} started, ${failures.length} failed to start DQ.`
          );
        }
        track(eventName, { total, failed: failures.length });
      } finally {
        setBulkProgress(null);
      }
    },
    [bulkProgress, handleRunDataQuality, track]
  );

  const selectionTargets = useMemo(() => {
    const { targets } = buildCurrentBulkTargets();
    const busy = new Set(Object.keys(pendingPipelineActionByFileId));
    return {
      validateEligibleIds: targets.runDqIds.filter((id) => !busy.has(id)),
      confirmEligibleIds: targets.confirmStructureIds.filter((id) => !busy.has(id)),
    };
  }, [buildCurrentBulkTargets, pendingPipelineActionByFileId]);

  useEffect(() => {
    setSelectedValidateIds((prev) =>
      prev.filter((id) => selectionTargets.validateEligibleIds.includes(id))
    );
  }, [selectionTargets]);

  useEffect(() => {
    setSelectedConfirmIds((prev) =>
      prev.filter((id) => selectionTargets.confirmEligibleIds.includes(id))
    );
  }, [selectionTargets]);

  const confirmStructureForFileId = useCallback(
    async (fileId: string): Promise<"confirmed" | "skipped" | "failed"> => {
      if (!beginPipelineAction(fileId, "confirm")) return "failed";
      try {
        const preview = await previewExtraction(fileId);
        recordFileConfidence(fileId, preview.globalConfidence, preview.requiresReview);
        if (preview.requiresReview) {
          return "skipped";
        }

        const worksheetsNormalized = normalizeWorksheetPreviewMetadata(preview.worksheets);
        const selection = buildDefaultMappingSelection(worksheetsNormalized);
        if (countSelectedMappingWorksheets(worksheetsNormalized, selection) < 1) {
          return "skipped";
        }

        await saveMappingSelection(
          fileId,
          buildMappingSelectionPayload(worksheetsNormalized, selection)
        );
        recordFilePreviewMetrics(fileId, worksheetsNormalized, selection);
        setStructureConfirmedFileIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
        setSingleFileExtractionCategory("succeeded");

        const cleanedKey = stageKey(fileId, "structure_confirmed");
        if (!cleanedStagesRef.current.has(cleanedKey)) {
          cleanedStagesRef.current.add(cleanedKey);
          void cleanupFileArtifacts(fileId, "structure_confirmed").catch((err) => {
            trackError(err, { context: "cleanup_structure_confirmed", fileId });
          });
        }
        return "confirmed";
      } catch (err) {
        trackError(err, { context: "confirm_structure_bulk", fileId });
        return "failed";
      } finally {
        finishPipelineAction(fileId);
      }
    },
    [
      beginPipelineAction,
      finishPipelineAction,
      recordFileConfidence,
      recordFilePreviewMetrics,
      trackError,
    ]
  );

  const confirmStructureForIds = useCallback(
    async (ids: string[]) => {
      if (bulkProgress || ids.length === 0) return;

      setFlowError("");
      setFlowInfo("");
      const total = ids.length;
      let completed = 0;
      let confirmed = 0;
      const skipped: string[] = [];
      const failed: string[] = [];
      setBulkProgress({ label: `Confirming structure 0 of ${total}…` });

      try {
        await runWithConcurrencyLimit(ids, BULK_ACTION_CONCURRENCY, async (fileId) => {
          const result = await confirmStructureForFileId(fileId);
          if (result === "confirmed") confirmed += 1;
          else if (result === "skipped") skipped.push(fileId);
          else failed.push(fileId);
          completed += 1;
          setBulkProgress({ label: `Confirming structure ${completed} of ${total}…` });
        });

        const nameById = new Map(batchFileMeta.map((entry) => [entry.fileId, entry.fileName]));
        const labelFor = (fileId: string) => nameById.get(fileId) ?? fileId;
        const parts: string[] = [];
        if (confirmed > 0) {
          parts.push(`Confirmed ${confirmed} file${confirmed === 1 ? "" : "s"}`);
        }
        if (skipped.length > 0) {
          parts.push(
            `Skipped ${skipped.length} (review required or no mapping regions): ${skipped
              .map(labelFor)
              .join(", ")}`
          );
        }
        if (failed.length > 0) {
          parts.push(`Failed ${failed.length}: ${failed.map(labelFor).join(", ")}`);
        }
        if (skipped.length > 0 || failed.length > 0) {
          setFlowInfo(parts.join(". ") + ".");
        }
        if (failed.length > 0 && confirmed === 0 && skipped.length === 0) {
          setFlowError(`Could not confirm structure for ${failed.length} file(s).`);
        }
        track("pipeline_confirm_structure_selected", {
          total,
          confirmed,
          skipped: skipped.length,
          failed: failed.length,
        });
      } finally {
        setBulkProgress(null);
      }
    },
    [batchFileMeta, bulkProgress, confirmStructureForFileId, track]
  );

  const handleRunDqSelected = useCallback(async () => {
    const ids = selectedValidateIds.filter((id) =>
      selectionTargets.validateEligibleIds.includes(id)
    );
    setSelectedValidateIds([]);
    await startDqForIds(ids, "pipeline_run_dq_selected");
  }, [selectedValidateIds, selectionTargets.validateEligibleIds, startDqForIds]);

  const handleConfirmStructureSelected = useCallback(async () => {
    const ids = selectedConfirmIds.filter((id) => selectionTargets.confirmEligibleIds.includes(id));
    setSelectedConfirmIds([]);
    await confirmStructureForIds(ids);
  }, [selectedConfirmIds, selectionTargets.confirmEligibleIds, confirmStructureForIds]);

  const validateSelection = useMemo<PipelineColumnSelection>(
    () => ({
      selectedIds: selectedValidateIds,
      eligibleIds: selectionTargets.validateEligibleIds,
      onToggle: (fileId: string) =>
        setSelectedValidateIds((prev) =>
          prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
        ),
      onToggleAll: () =>
        setSelectedValidateIds((prev) =>
          prev.length === selectionTargets.validateEligibleIds.length
            ? []
            : selectionTargets.validateEligibleIds
        ),
      onRunSelected: () => void handleRunDqSelected(),
      runLabel: `Run DQ selected (${selectedValidateIds.length})`,
      busy: bulkProgress != null,
    }),
    [bulkProgress, handleRunDqSelected, selectedValidateIds, selectionTargets.validateEligibleIds]
  );

  const confirmSelection = useMemo<PipelineColumnSelection>(
    () => ({
      selectedIds: selectedConfirmIds,
      eligibleIds: selectionTargets.confirmEligibleIds,
      onToggle: (fileId: string) =>
        setSelectedConfirmIds((prev) =>
          prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
        ),
      onToggleAll: () =>
        setSelectedConfirmIds((prev) =>
          prev.length === selectionTargets.confirmEligibleIds.length
            ? []
            : selectionTargets.confirmEligibleIds
        ),
      onRunSelected: () => void handleConfirmStructureSelected(),
      runLabel: `Confirm selected (${selectedConfirmIds.length})`,
      busy: bulkProgress != null,
    }),
    [
      bulkProgress,
      handleConfirmStructureSelected,
      selectedConfirmIds,
      selectionTargets.confirmEligibleIds,
    ]
  );

  const handleRetryFailures = useCallback(async () => {
    if (bulkProgress) return;
    const { meta, targets } = buildCurrentBulkTargets();
    if (targets.retryFailureCount === 0) return;

    setFlowError("");
    setFlowInfo("");

    try {
      if (targets.retryIngestIds.length > 0) {
        setBulkProgress({ label: "Retrying ingestion…" });
        setIsTriggeringIngestion(true);
        setIsPollingBatch(true);
        setActiveWorkflowFileCount(targets.retryIngestIds.length);

        const retryMeta = meta.filter((entry) => targets.retryIngestIds.includes(entry.fileId));
        const existingStatus = batchStatus;
        const primaryBatchId = batchId ?? BATCH_STARTING_PLACEHOLDER_ID;

        const triggerResult = await triggerBatchExtraction(targets.retryIngestIds);
        const startingRetry = createStartingBatchStatus(retryMeta, triggerResult.batchId);
        const pollingStatus = mergeBatchExtractionStatuses(existingStatus, startingRetry, {
          primaryBatchId,
          replaceExistingFileIds: true,
        });
        setBatchStatus(pollingStatus);
        setWorkflowBatchStatus(startingRetry);
        setBatchId(primaryBatchId);

        const abortController = new AbortController();
        batchAbortRef.current = abortController;
        const finalStatus = await pollBatchUntilComplete(
          triggerResult.batchId,
          abortController.signal,
          {
            expectedFileCount: targets.retryIngestIds.length,
            mergeWith: existingStatus,
            mergeOptions: { primaryBatchId, replaceExistingFileIds: true },
            onWorkflowStatus: setWorkflowBatchStatus,
          }
        );
        const mergedStatus = mergeBatchExtractionStatuses(existingStatus, finalStatus, {
          primaryBatchId,
          replaceExistingFileIds: true,
        });
        setBatchStatus(mergedStatus);
        void prefetchPipelinePreviewMetrics(mergedStatus);

        if (!abortController.signal.aborted) {
          batchAbortRef.current = null;
        }
        setIsTriggeringIngestion(false);
        setIsPollingBatch(false);
        setActiveWorkflowFileCount(0);
        setWorkflowBatchStatus(null);
      }

      if (targets.retryDqIds.length > 0) {
        const total = targets.retryDqIds.length;
        let completed = 0;
        const failures: string[] = [];
        setBulkProgress({ label: `Retrying DQ 0 of ${total}…` });

        await runWithConcurrencyLimit(
          targets.retryDqIds,
          BULK_ACTION_CONCURRENCY,
          async (fileId) => {
            const ok = await handleRunDataQuality(fileId);
            if (!ok) failures.push(fileId);
            completed += 1;
            setBulkProgress({ label: `Retrying DQ ${completed} of ${total}…` });
          }
        );

        if (failures.length > 0) {
          setFlowError(
            `DQ retry: ${total - failures.length} started, ${failures.length} failed to start.`
          );
        }
      }

      track("pipeline_bulk_retry_failures", {
        ingestCount: targets.retryIngestIds.length,
        dqCount: targets.retryDqIds.length,
      });
    } catch (err) {
      if (!isAbortError(err)) {
        setFlowError(`Retry failures failed: ${err instanceof Error ? err.message : String(err)}`);
        trackError(err, { context: "pipeline_bulk_retry_failures" });
      }
    } finally {
      setIsTriggeringIngestion(false);
      setIsPollingBatch(false);
      setActiveWorkflowFileCount(0);
      setWorkflowBatchStatus(null);
      setBulkProgress(null);
    }
  }, [
    batchId,
    batchStatus,
    buildCurrentBulkTargets,
    bulkProgress,
    handleRunDataQuality,
    pollBatchUntilComplete,
    prefetchPipelinePreviewMetrics,
    track,
    trackError,
  ]);

  const previewSchemaId = uploadedDocumentId
    ? resolveFileSchemaId(uploadedDocumentId)
    : defaultSchemaId;
  const previewSchemaName = schemas.find((s) => s.id === previewSchemaId)?.name ?? previewSchemaId;
  const requiresReportType = schemaRequiresReportType(previewSchemaName);
  const batchWorkflowActive =
    isBatchMode && (isUploading || isTriggeringIngestion || isPollingBatch);

  const pipelineFileMeta = useMemo((): PipelineFileMeta[] => {
    if (batchFileMeta.length > 0) return batchFileMeta;
    if (uploadedDocumentId) {
      return [
        {
          fileId: uploadedDocumentId,
          fileName: previewFileName || file?.name || uploadedDocumentId,
          schemaId: uploadedFileSchemaId || defaultSchemaId,
          reportType: uploadedFileReportType,
          allowedReportTypes: uploadedFileAllowedReportTypes,
          providerId: sessionProviderIdRef.current,
          uploadedBy: currentUser?.userId ?? null,
        },
      ];
    }
    return [];
  }, [
    batchFileMeta,
    currentUser?.userId,
    defaultSchemaId,
    uploadedDocumentId,
    uploadedFileSchemaId,
    uploadedFileReportType,
    uploadedFileAllowedReportTypes,
    previewFileName,
    file,
  ]);

  const handleDownloadMappedFile = useCallback(
    async (fileId: string) => {
      if (!beginPipelineAction(fileId, "download")) return;
      const meta = pipelineFileMeta.find((f) => f.fileId === fileId);
      const originalName = meta?.fileName?.trim() || fileId;
      const stem = originalName.includes(".")
        ? originalName.slice(0, originalName.lastIndexOf("."))
        : originalName;
      const fallbackName = `${stem || fileId}_mapped.xlsx`;
      try {
        await downloadMappedFileXlsx(fileId, fallbackName);
        track("pipeline_mapped_file_downloaded", { fileId });
      } catch (err) {
        setFlowError(
          `Could not download mapped file: ${err instanceof Error ? err.message : String(err)}`
        );
        trackError(err, { context: "download_mapped_file", fileId });
      } finally {
        finishPipelineAction(fileId);
      }
    },
    [beginPipelineAction, finishPipelineAction, pipelineFileMeta, track, trackError]
  );

  const schemaNameByFileId = useMemo(
    () => buildSchemaNameByFileId(pipelineFileMeta, pickerSchemaNameById),
    [pipelineFileMeta, pickerSchemaNameById]
  );
  const uploadingFileModels = useMemo(
    () =>
      uploadingFiles.map((entry) => ({
        fileId: entry.fileId ?? entry.key,
        fileName: entry.fileName,
        schemaName: resolvePipelineSchemaDisplayName(
          entry.schemaId,
          entry.schemaName,
          pickerSchemaNameById
        ),
        status: entry.status,
        providerId: entry.providerId ?? null,
        uploadedBy: entry.uploadedBy ?? currentUser?.userId ?? null,
      })),
    [currentUser?.userId, pickerSchemaNameById, uploadingFiles]
  );

  const dqxDialogRuns = useMemo(
    () => listPipelineDqxDialogRuns(dqxDialogFileId ? pipelineDqxByFileId[dqxDialogFileId] : null),
    [dqxDialogFileId, pipelineDqxByFileId]
  );
  const dqxDialogBoardRuns = useMemo(
    () =>
      dqxDialogRuns
        .filter((run) => Boolean(run.jobId?.trim()))
        .map((run) => ({
          regionId: run.regionId ?? null,
          runId: run.runId,
          jobId: run.jobId,
          sourceRowIndexStart: run.sourceRowIndexStart ?? null,
          totalRowsChecked: run.totalRowsChecked ?? null,
        })),
    [dqxDialogRuns]
  );

  const mappingInitialDqxRuns = useMemo((): MappingWorkbenchDqxRun[] => {
    const fileId = uploadDocumentId?.trim();
    if (!fileId) return [];
    return pipelineDqxRunsToWorkbenchRuns(listPipelineDqxDialogRuns(pipelineDqxByFileId[fileId]));
  }, [uploadDocumentId, pipelineDqxByFileId]);

  const singleFileBoard =
    !isBatchMode && uploadedDocumentId && files.length === 1
      ? {
          fileId: uploadedDocumentId,
          fileName: previewFileName || file!.name,
          category:
            singleFileExtractionCategory ??
            (isTriggeringIngestion || isUploading ? ("running" as const) : ("pending" as const)),
          stateMessage: ingestionStatusLabel,
          isExtracting: isTriggeringIngestion || isUploading,
          uploadedBy: currentUser?.userId ?? null,
        }
      : null;

  const previewDialogOnConfirm =
    worksheets.length === 0 && !requiresReview && !isBatchMode
      ? handleTriggerIngestion
      : handleConfirmStructure;

  return (
    <>
      <PipelineBoard
        batchStatus={batchStatus}
        fileMeta={pipelineFileMeta}
        schemaNameByFileId={schemaNameByFileId}
        mappedFileIds={mappedFileIds}
        structureConfirmedFileIds={structureConfirmedFileIds}
        previewingFileId={previewingFileId}
        pendingActionByFileId={pendingPipelineActionByFileId}
        rowCountByFileId={fileRowCounts}
        tableCountByFileId={fileTableCounts}
        globalConfidenceByFileId={fileGlobalConfidence}
        requiresReviewByFileId={fileRequiresReview}
        dqxStateByFileId={pipelineDqxByFileId}
        uploadingFiles={uploadingFileModels}
        validateSelection={validateSelection}
        confirmSelection={confirmSelection}
        error={boardError}
        infoMessage={flowInfo || undefined}
        onDismissError={() => {
          setFlowError("");
          if (error) setDismissedBoardError(error);
        }}
        singleFile={singleFileBoard}
        currentUser={
          currentUser ? { userId: currentUser.userId, userEmail: currentUser.userEmail } : null
        }
        onAddFiles={openAddFilesDialog}
        onConfirmStructure={(fileId) => void handleBatchPreview(fileId)}
        onContinueMapping={(fileId) => void handleContinueToMapping(fileId)}
        onPreview={(fileId) => void handleBatchPreview(fileId)}
        onRemoveFile={handleRequestRemovePipelineFile}
        onRunDataQuality={(fileId) => void handleRunDataQuality(fileId)}
        onViewDqxResults={(fileId) => void handleViewDqxResults(fileId)}
        onDownloadMapped={(fileId) => void handleDownloadMappedFile(fileId)}
        onRetryFailures={() => void handleRetryFailures()}
        bulkProgress={bulkProgress}
        onCancelBatch={() => void handleCancelBatch()}
        isPollingBatch={isPollingBatch}
        isUploading={isUploading}
        isRestoringBoard={isRestoringBoard}
        isTriggeringIngestion={isTriggeringIngestion}
        workflowFileCount={activeWorkflowFileCount}
        uploadedWorkflowFileCount={uploadedWorkflowFileCount}
        workflowBatchStatus={workflowBatchStatus}
        singleFileStatusLabel={ingestionStatusLabel}
        adminFilters={
          showProviderBoardFilter
            ? {
                providerId: boardFilterProviderId,
                onProviderIdChange: setBoardFilterProviderId,
                ...(isBoardAdmin
                  ? {
                      uploadedBy: boardFilterUploadedBy,
                      onUploadedByChange: setBoardFilterUploadedBy,
                      showUserFilter: true,
                    }
                  : { showUserFilter: false }),
              }
            : null
        }
      />

      <ConfirmDialog
        open={deleteFileTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteFileTarget(null);
        }}
        title={
          deleteFileTarget?.stage === "published"
            ? "Remove published file from workspace?"
            : "Delete file?"
        }
        description={
          deleteFileTarget?.stage === "published" ? (
            <p>
              <span className="font-mono font-medium">{deleteFileTarget?.fileName}</span> will be
              removed from the workspace. Its published gold output, mapped data, and history are
              kept.
            </p>
          ) : (
            <>
              <p>
                <span className="font-mono font-medium">{deleteFileTarget?.fileName}</span> will be
                removed from the workspace and permanently deleted from storage,{" "}
                {DELETE_SCOPE_BY_STAGE[deleteFileTarget?.stage ?? "upload"]}.
              </p>
              <p className="mt-2">Approved column mappings learned from this file are kept.</p>
            </>
          )
        }
        confirmLabel={deleteFileTarget?.stage === "published" ? "Remove card" : "Delete file"}
        tone={deleteFileTarget?.stage === "published" ? "warning" : "danger"}
        onConfirm={handleConfirmDeleteFile}
      />

      <AnimatedDialog
        open={mappingDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeMappingDialog();
        }}
        maxWidth="max-w-[85rem]"
        className="h-[92vh] max-h-[calc(100vh-2rem)] overflow-hidden p-0"
      >
        {/* Close stays on non-scrolling chrome; only the body overflows. */}
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <Dialog.Title className="sr-only">Column mapping</Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 z-40 flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-[var(--color-text-tertiary)] shadow-sm backdrop-blur-sm hover:bg-white hover:text-[var(--color-text-primary)]"
              aria-label="Close"
            >
              <X className="h-5 w-5" strokeWidth={1.8} aria-hidden />
            </button>
          </Dialog.Close>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <MappingWorkbench
              dialogInset
              initialDqxRuns={mappingInitialDqxRuns}
              onExitToBoard={closeMappingDialog}
              onContinueToDataQuality={openDqxDialogFromMapping}
              onFileMapped={handleFileMapped}
            />
          </div>
        </div>
      </AnimatedDialog>

      <AnimatedDialog
        open={dqxDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDqxDialog();
        }}
        maxWidth="max-w-[85rem]"
        className="h-[92vh] max-h-[calc(100vh-2rem)] overflow-hidden p-0"
      >
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <Dialog.Title className="sr-only">Data quality results</Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 z-40 flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-[var(--color-text-tertiary)] shadow-sm backdrop-blur-sm hover:bg-white hover:text-[var(--color-text-primary)]"
              aria-label="Close"
            >
              <X className="h-5 w-5" strokeWidth={1.8} aria-hidden />
            </button>
          </Dialog.Close>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <DataQualityView
              key={dqxDialogFileId || "dqx"}
              dialogInset
              autoRetrieve={dqxDialogAutoRetrieve}
              startWithStatusCheckSucceeded={dqxDialogAutoRetrieve}
              initialTableIndex={dqxDialogInitialTableIndex}
              onDqxRerunStarted={handleDqxRerunStarted}
              onExitToBoard={closeDqxDialog}
              onProceedToKnownMappings={() => {
                setDqxDialogOpen(false);
                setKnownMappingsDialogProviderId(sessionProviderIdRef.current ?? undefined);
                setKnownMappingsDialogOpen(true);
              }}
              boardFileId={dqxDialogFileId}
              boardSchemaId={dqxDialogFileId ? resolveFileSchemaId(dqxDialogFileId) : null}
              boardDqxRuns={dqxDialogBoardRuns}
            />
          </div>
        </div>
      </AnimatedDialog>

      <AnimatedDialog
        open={knownMappingsDialogOpen}
        onOpenChange={setKnownMappingsDialogOpen}
        maxWidth="max-w-[85rem]"
        className="h-[92vh] max-h-[calc(100vh-2rem)] overflow-hidden p-0"
      >
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <Dialog.Title className="sr-only">Known mappings</Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 z-40 flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-[var(--color-text-tertiary)] shadow-sm backdrop-blur-sm hover:bg-white hover:text-[var(--color-text-primary)]"
              aria-label="Close"
            >
              <X className="h-5 w-5" strokeWidth={1.8} aria-hidden />
            </button>
          </Dialog.Close>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6">
            <KnownMappingsPage providerId={knownMappingsDialogProviderId} />
          </div>
        </div>
      </AnimatedDialog>

      <AddFilesDialog
        open={addFilesDialogOpen}
        onOpenChange={setAddFilesDialogOpen}
        pendingEntries={pendingAddEntries}
        onPendingFilesAdd={handlePendingFilesAdd}
        onPendingEntrySchemaChange={handlePendingEntrySchemaChange}
        onPendingAllSchemasChange={handlePendingAllSchemasChange}
        onPendingEntryReportTypeChange={handlePendingEntryReportTypeChange}
        onPendingEntryRemove={handlePendingEntryRemove}
        onUploadError={(message) => {
          setFlowError(message);
          track("upload_error_displayed", { message });
        }}
        schemas={addFilesSchemas}
        isLoadingSchemas={isLoadingSchemas}
        onSubmit={() => void handleBeginConversion()}
        isSubmitting={isUploading}
        error={error}
        showCancelBatch={batchWorkflowActive}
        onCancelBatch={() => void handleCancelBatch()}
        providerRequired={true}
        providerId={addFilesProviderId}
        onProviderChange={handleAddFilesProviderChange}
      />

      <FilePreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        fileName={previewFileName || file?.name || ""}
        schemaName={previewSchemaName}
        worksheets={worksheets}
        onConfirm={() => {
          void previewDialogOnConfirm();
        }}
        onCancel={() => {
          track("upload_preview_cancelled", { schemaId: previewSchemaId });
          setShowPreview(false);
        }}
        isUploading={isTriggeringIngestion}
        isConfirming={false}
        ingestionStatusLabel={ingestionStatusLabel}
        confirmLabel={
          worksheets.length === 0 && !requiresReview && !isBatchMode
            ? "Trigger Ingestion"
            : worksheets.length > 0
              ? "Confirm structure"
              : undefined
        }
        confirmPrompt={
          worksheets.length === 0 && !requiresReview
            ? isBatchMode
              ? undefined
              : "Ready to trigger ingestion and load extraction preview?"
            : worksheets.length > 0
              ? "Confirm detected tables and regions before mapping."
              : undefined
        }
        requiresReportType={requiresReportType}
        reportType={reportType}
        detectedType={detectedType}
        allowedReportTypes={allowedReportTypes}
        isClassifying={isClassifying}
        onReportTypeChange={setReportType}
        regionReportTypes={regionReportTypes}
        onRegionReportTypeChange={(regionId, value) =>
          setRegionReportTypes((prev) => ({ ...prev, [regionId]: value }))
        }
        requiresReview={requiresReview}
        reviewReason={reviewReason}
        reviewThreshold={reviewThreshold}
        globalConfidence={globalConfidence}
        processingDurationMs={processingDurationMs}
        ingestionTriggerSkipped={ingestionTriggerSkipped}
        executionMode={executionMode}
        mappingSelection={mappingSelection}
        onMappingSelectionChange={(previewKey, included) =>
          setMappingSelection((prev) => ({ ...prev, [previewKey]: included }))
        }
      />
    </>
  );
}
